import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  MessageSquare, 
  Search, 
  FileText, 
  Download, 
  Copy, 
  ArrowLeft, 
  AlertCircle
} from 'lucide-react';
import { useResearchStore } from '../store/researchStore';
import { useDashboardData, useGenerateContent } from '../api/researchApi';
import { toast } from 'react-hot-toast';
import { generateResearchPDF } from '../utils/pdfGenerator';

const CustomUserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 0C13.5913 0 15.1174 0.632141 16.2426 1.75736C17.3679 2.88258 18 4.4087 18 6C18 7.5913 17.3679 9.11742 16.2426 10.2426C15.1174 11.3679 13.5913 12 12 12C10.4087 12 8.88258 11.3679 7.75736 10.2426C6.63214 9.11742 6 7.5913 6 6C6 4.4087 6.63214 2.88258 7.75736 1.75736C8.88258 0.632141 10.4087 0 12 0ZM12 15C18.63 15 24 17.685 24 21V24H0V21C0 17.685 5.37 15 12 15Z" fill="#4A3B8E"/>
  </svg>
);

interface TabConfig {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}



interface ProgressItem {
  feature: string;
  percentage: number;
  impact: 'High' | 'Medium' | 'Low';
  color: 'green' | 'yellow' | 'red';
}

const TAB_CONFIGS: TabConfig[] = [
  { id: 'overview', label: 'Actions', icon: BarChart3 },
  { id: 'demographics', label: 'Demographics', icon: CustomUserIcon },
  { id: 'competitor', label: 'Competitor Analysis', icon: TrendingUp },
  { id: 'reviews', label: 'Reviews', icon: MessageSquare },
  { id: 'discovery', label: 'Product Discovery', icon: Search },
  { id: 'keywords', label: 'Keywords', icon: FileText },
];





const MOCK_PROJECT_DATA = {
  id: 'demo-project-123',
  name: 'Product Name',
  asin: 'B0B1D2G1Y6',
  brandName: 'ADPL EXPOSITION',
  productUrl: 'https://www.amazon.com/dp/B0B1D2G1Y6',
  targetAudience: 'Puzzle enthusiasts, art lovers, families',
  tone: 'Premium, artistic, engaging',
  goals: 'Increase conversions, improve listing visibility',
  createdAt: '2024-01-15T10:30:00Z',
  updatedAt: '2024-01-15T10:30:00Z',
} as const;

const getImpactColorClasses = (color: string, isText = false) => {
  const baseClasses = isText ? 'text-white' : 'bg-gray-100 text-gray-700';
  const colorClasses = {
    green: isText ? 'bg-green-500' : 'bg-green-100 text-green-700',
    yellow: isText ? 'bg-yellow-500' : 'bg-yellow-100 text-yellow-700',
    red: isText ? 'bg-red-500' : 'bg-red-100 text-red-700',
    High: isText ? 'text-gray-800' : 'text-gray-800',
    Medium: isText ? 'text-gray-800' : 'text-gray-800',
    Low: isText ? 'text-gray-800' : 'text-gray-800',
  };
  return colorClasses[color as keyof typeof colorClasses] || baseClasses;
};

const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600 mx-auto" />
      <p className="mt-4 text-gray-600">Loading research data...</p>
    </div>
  </div>
);

const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
      <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Data</h2>
      <p className="text-gray-600 mb-4">Failed to load research data. Please try again.</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
      >
        Retry
      </button>
    </div>
  </div>
);

// const DataValidationInfo: React.FC<{ data: any }> = ({ data }) => {
//   if (!data) return null;
//   
//   const reports = data.reports || {};
//   const availableReports = Object.keys(reports);
//   const completedReports = availableReports.filter(key => reports[key]?.status === 'completed');
//   
//   return (
//     <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
//       <div className="flex items-center space-x-2 mb-2">
//         <AlertCircle className="h-5 w-5 text-blue-600" />
//         <h3 className="text-sm font-medium text-blue-800">Data Status</h3>
//       </div>
//       <div className="text-sm text-blue-700">
//         <p>Available Reports: {availableReports.length}</p>
//         <p>Completed Reports: {completedReports.length}</p>
//         {availableReports.length > 0 && (
//           <p className="mt-1">
//             Reports: {availableReports.join(', ')}
//           </p>
//         )}
//       </div>
//     </div>
//   );
// };

const ProductImage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: dashboardData } = useDashboardData(projectId ? parseInt(projectId) : null);
  const projectImage = (dashboardData?.project as any)?.primary_image;
  
  if (projectImage?.image_url) {
    const imageUrl = `${(import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'}${projectImage.image_url}`;
    return (
      <div className="bg-white rounded-lg flex items-center justify-center border-2 border-pink-400 shadow-sm relative overflow-hidden" style={{ width: '160px', height: '180px', flexShrink: 0 }}>
        <img 
          src={imageUrl} 
          alt="Product" 
          className="w-full h-full object-contain"
          onError={(e) => {
            // Fallback to placeholder if image fails to load
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <div className="text-gray-400 text-center hidden">
          <div className="text-2xl mb-1">📦</div>
          <div className="text-xs font-medium">Product Image</div>
          <div className="text-xs text-gray-300">Product</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg flex items-center justify-center border-2 border-pink-400 shadow-sm relative overflow-hidden" style={{ width: '160px', height: '180px', flexShrink: 0 }}>
      <div className="text-gray-400 text-center">
        <div className="text-2xl mb-1">📦</div>
        <div className="text-xs font-medium">Product Image</div>
        <div className="text-xs text-gray-300">Product</div>
      </div>
    </div>
  );
};

const ProgressCircle: React.FC<{ percentage: string; color: string; size?: 'sm' | 'md' }> = ({ 
  percentage, 
  color, 
  size = 'md' 
}) => {
  const sizeClasses = {
    sm: { width: 66, height: 66 },
    md: { width: 66, height: 66 }
  };
  
  const colorClasses = {
    purple: '#8B5CF6',
    green: '#10B981',
    red: '#EF4444'
  };

  const numericPercentage = parseInt(percentage.replace('%', ''));
  const circumference = 2 * Math.PI * 25; // radius of 25 for 66px circle
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (numericPercentage / 100) * circumference;

  return (
    <div className="relative" style={sizeClasses[size]}>
      <svg width="66" height="66" className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="33"
          cy="33"
          r="25"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx="33"
          cy="33"
          r="25"
          fill="none"
          stroke={colorClasses[color as keyof typeof colorClasses]}
          strokeWidth="8"
          strokeLinecap="butt"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-${color}-600 font-semibold`} style={{ fontSize: '14px' }}>
        {percentage}
      </span>
      </div>
    </div>
  );
};

const ActionButton: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}> = ({ onClick, disabled, variant = 'secondary', children, icon: Icon }) => {
  const baseClasses = "px-6 py-3 rounded-lg font-medium transition flex items-center space-x-2";
  const variantClasses = {
    primary: "text-white hover:opacity-90 cursor-pointer",
    secondary: "bg-white border border-purple-600 text-purple-700 hover:bg-purple-50"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={variant === 'primary' ? {
        borderRadius: '12px',
        background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)',
        display: 'flex',
        width: '350px',
        height: '48px',
        padding: '16px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px'
      } : undefined}
    >
      {Icon && <Icon className="h-4 w-4" />}
      <span>{children}</span>
    </button>
  );
};

const TabButton: React.FC<{
  tab: TabConfig;
  isActive: boolean;
  onClick: () => void;
}> = ({ tab: { id, label, icon: Icon }, isActive, onClick }) => (
  <button
    key={id}
    onClick={onClick}
    className={`flex items-center space-x-2 py-4 px-6 font-medium text-sm transition ${
      isActive ? "text-white" : "text-gray-600 hover:text-gray-800"
    }`}
    style={{
      borderRadius: '12px',
      background: isActive 
        ? 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
        : 'transparent',
      border: isActive ? 'none' : '1px solid #E2D5FF'
    }}
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
  </button>
);

const SuggestionCard: React.FC<{
  title: string;
  description: string;
  percentage: string;
  color: string;
  tags: string[];
  impact?: string;
  impactText?: string;
}> = ({ title, description, percentage, color, tags, impact, impactText }) => (
  <div className="p-6 h-full relative" style={{ borderRadius: '10px', border: '1px solid #D8C6FF', background: '#Fff' }}>
    {/* Priority badge in top right corner */}
    {impact && (
      <div className="absolute top-4 right-4">
        <span 
          className="text-xs"
          style={{
            borderRadius: '20px',
            padding: '6px 14px',
            background: impact === 'High' ? '#DAFFD0' : 
                       impact === 'Medium' ? '#FFFDD0' : 
                       impact === 'Low' ? '#FFE1E2' : '#DAFFD0',
            color: impact === 'High' ? '#279808' : 
                   impact === 'Medium' ? '#A69E00' : 
                   impact === 'Low' ? '#D50E0E' : '#279808'
          }}
        >
          {impact}
        </span>
      </div>
    )}
    
    <div className="flex flex-col items-start">
      {/* Icon on its own row */}
      <div className="mb-4">
        <ProgressCircle percentage={percentage} color={color} />
      </div>
      
      {/* Content in row alignment */}
      <div className="w-full">
        <h4 className="font-semibold mb-2 text-left" style={{ color: '#3D348B' }}>{title}</h4>
        <p className="text-sm mb-2 text-left" style={{ color: '#3D348B' }}>{description}</p>
        {impactText && (
          <p className="text-xs mb-3 text-left" style={{ color: '#6B7280', fontStyle: 'italic' }}>Impact: {impactText}</p>
        )}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <span key={index} className="px-2 py-1 rounded" style={{ 
                borderRadius: '20px',
                border: '1px solid #D8C6FF',
                color: '#3D348B',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
                textTransform: 'capitalize'
              }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProgressBar: React.FC<{ percentage: number; color?: string }> = ({ percentage, color = 'blue' }) => (
  <div className="w-24 bg-gray-200 rounded-full h-2">
    <div 
      className={`bg-${color}-600 h-2 rounded-full transition-all duration-300`} 
      style={{ width: `${percentage}%` }}
    />
  </div>
);

const ProgressItem: React.FC<{ item: ProgressItem }> = ({ item }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-700">{item.feature}</span>
    <div className="flex items-center space-x-3">
      <ProgressBar percentage={item.percentage} />
      <span className="text-sm font-medium text-gray-900">{item.percentage}%</span>
      <span className={`px-2 py-1 text-xs rounded text-white ${getImpactColorClasses(item.color, true)}`}>
        {item.impact}
      </span>
    </div>
  </div>
);

const ResearchAgentDashboard: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentProject, setCurrentProject } = useResearchStore();
  const { data: dashboardData, isLoading: apiLoading, error: apiError, refetch } = useDashboardData(projectId ? parseInt(projectId) : null);
  
  // Use API data directly for proper backend data structure
  const isLoading = apiLoading;
  const error = apiError;
  const displayData = dashboardData;
  
  // Debug logging
  console.log('Reports structure debug:', {
    hasReports: !!displayData?.reports,
    reportsKeys: displayData?.reports ? Object.keys(displayData.reports) : [],
    reportsContent: displayData?.reports ? Object.fromEntries(
      Object.entries(displayData.reports).map(([key, value]: [string, any]) => [
        key, 
        value?.content ? Object.keys(value.content).slice(0, 5) : 'No content'
      ])
    ) : 'No reports'
  });
  
  // Enhanced data access with fallbacks for missing fields
  // const getReportData = (reportType: string, fieldPath: string, fallback: any = null) => {
  //   const report = displayData?.reports?.[reportType]?.content;
  //   if (!report) return fallback;
  //   
  //   // Handle nested field paths like 'keyword_analysis.primary_keywords'
  //   const pathParts = fieldPath.split('.');
  //   let current = report;
  //   
  //   for (const part of pathParts) {
  //     if (current && typeof current === 'object' && part in current) {
  //       current = current[part];
  //     } else {
  //       return fallback;
  //     }
  //   }
  //   
  //   return current || fallback;
  // };
  const generateContent = useGenerateContent();

  const [activeTab, setActiveTab] = useState('overview');
  const [isProcessing, setIsProcessing] = useState(false);
  
  const isDemoMode = useMemo(() => !projectId, [projectId]);

  // No need to fetch store data since we're using API data directly
  // useEffect(() => {
  //   if (projectId && !researchData) {
  //     fetchResearchData(projectId);
  //   }
  // }, [projectId, researchData, fetchResearchData]);

  const handleGenerateContent = useCallback(async () => {
    if (isDemoMode) {
      // Navigate to copy agent page for demo mode
      navigate(`/copy/${projectId || 'demo'}`);
      return;
    }
    
    if (!projectId) return;
    
    try {
      setIsProcessing(true);
      await generateContent.mutateAsync({ 
        project_id: parseInt(projectId),
        report_types: ['overview', 'demographics', 'competitor', 'reviews', 'discovery', 'keywords']
      });
      toast.success('Content generation started successfully!');
      // Navigate to copy agent page after successful generation
      navigate(`/copy/${projectId || 'demo'}?autoStart=true`);
    } catch (error) {
      toast.error('Failed to start content generation');
    } finally {
      setIsProcessing(false);
    }
  }, [isDemoMode, projectId, generateContent, navigate]);

  const handleDownloadResearch = useCallback(async () => {
    try {
      // Generate PDF using the utility function
      if (!displayData) {
        return;
      }
      if (!currentProject) {
        return;
      }
      const doc = generateResearchPDF(displayData, currentProject);
      
      // Download the PDF
      const fileName = `research-report-${(currentProject?.name || 'product').replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('Research report PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to generate PDF report');
    }
  }, [currentProject, displayData]);

  const handleBackToDashboard = useCallback(() => {
    navigate('/research-landing');
  }, [navigate]);

  useEffect(() => {
    if (isDemoMode) {
      setCurrentProject(MOCK_PROJECT_DATA);
    } else if (dashboardData?.project) {
      const projectData = dashboardData.project;
      setCurrentProject({
        id: projectData.id.toString(),
        name: projectData.project_name || 'Mock Product Name',
        asin: projectData.amazon_listing_link ? extractASIN(projectData.amazon_listing_link) || 'B0B1D2G1Y6' : 'B0B1D2G1Y6',
        brandName: projectData.brand_name || 'Mock Brand',
        productUrl: projectData.amazon_listing_link || 'https://www.amazon.com/dp/B0B1D2G1Y6',
        targetAudience: projectData.product_info || 'Mock target audience',
        tone: 'Professional',
        goals: 'Increase sales and improve conversion',
        createdAt: projectData.created_at || '2024-01-15T10:30:00Z',
        updatedAt: projectData.created_at || '2024-01-15T10:30:00Z',
      });
    }
  }, [dashboardData, setCurrentProject, isDemoMode]);

  if (isLoading && !isDemoMode) {
    return <LoadingSpinner />;
  }

  if (error && !isDemoMode) {
    return <ErrorState onRetry={refetch} />;
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #D7BEFF 0%, #FDFDFF 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentProject && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 mb-8"
            style={{ 
              marginTop: '64px',
              borderRadius: '20px',
              border: '1px solid #4A3B8E',
              background: '#F4F3FF'
            }}
          >
            <div className="flex items-stretch space-x-4">
              <div className="flex-shrink-0">
                <ProductImage />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">{currentProject.name}</h2>
                  <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-lg text-sm font-medium">
                    ASIN: {currentProject.asin || 'B0B1D2G1Y6'}
                  </div>
                  <div className="bg-white border border-gray-200 text-gray-800 px-3 py-1 rounded-lg text-sm font-medium flex items-center space-x-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19 4H17V3C17 2.73478 16.8946 2.48043 16.7071 2.29289C16.5196 2.10536 16.2652 2 16 2C15.7348 2 15.4804 2.10536 15.2929 2.29289C15.1054 2.48043 15 2.73478 15 3V4H9V3C9 2.73478 8.89464 2.48043 8.70711 2.29289C8.51957 2.10536 8.26522 2 8 2C7.73478 2 7.48043 2.10536 7.29289 2.29289C7.10536 2.48043 7 2.73478 7 3V4H5C4.20435 4 3.44129 4.31607 2.87868 4.87868C2.31607 5.44129 2 6.20435 2 7V19C2 19.7956 2.31607 20.5587 2.87868 21.1213C3.44129 21.6839 4.20435 22 5 22H19C19.7956 22 20.5587 21.6839 21.1213 21.1213C21.6839 20.5587 22 19.7956 22 19V7C22 6.20435 21.6839 5.44129 21.1213 4.87868C20.5587 4.31607 19.7956 4 19 4ZM20 19C20 19.2652 19.8946 19.5196 19.7071 19.7071C19.5196 19.8946 19.2652 20 19 20H5C4.73478 20 4.48043 19.8946 4.29289 19.7071C4.10536 19.5196 4 19.2652 4 19V12H20V19ZM20 10H4V7C4 6.73478 4.10536 6.48043 4.29289 6.29289C4.48043 6.10536 4.73478 6 5 6H7V7C7 7.26522 7.10536 7.51957 7.29289 7.70711C7.48043 7.89464 7.73478 8 8 8C8.26522 8 8.51957 7.89464 8.70711 7.70711C8.89464 7.51957 9 7.26522 9 7V6H15V7C15 7.26522 15.1054 7.51957 15.2929 7.70711C15.4804 7.89464 15.7348 8 16 8C16.2652 8 16.5196 7.89464 16.7071 7.70711C16.8946 7.51957 17 7.26522 17 7V6H19C19.2652 6 19.5196 6.10536 19.7071 6.29289C19.8946 6.48043 20 6.73478 20 7V10Z" fill="#4A3B8E"/>
                    </svg>
                    <span>{currentProject.createdAt ? new Date(currentProject.createdAt).toLocaleDateString('en-GB') : '14-08-2025'}</span>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-6 max-w-2xl">
                  Your project officially kicks off once: This form is submitted, and We get access to your assets folder. This won't take more than 10 minutes.
                </p>
                
                <div className="flex space-x-3">
                  <ActionButton
                    onClick={handleGenerateContent}
                    disabled={generateContent.isPending || isProcessing}
                    variant="primary"
                    icon={Copy}
                  >
                    Generate Copy
                  </ActionButton>
                  
                  <ActionButton 
                    variant="secondary" 
                    icon={Download}
                    onClick={handleDownloadResearch}
                  >
                    Download Research
                  </ActionButton>
                  
                  <ActionButton 
                    variant="secondary" 
                    icon={ArrowLeft}
                    onClick={handleBackToDashboard}
                  >
                    Back To Dashboard
                  </ActionButton>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="bg-white rounded-lg shadow-sm border mb-8" style={{ borderRadius: '20px', padding: '24px' }}>
          <nav className="flex justify-between px-6 py-4 border-b border-gray-200">
            {TAB_CONFIGS.map((tab) => (
              <TabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              />
            ))}
          </nav>

          <div className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                        {activeTab === 'overview' && <OverviewTab researchData={displayData} />}
        {activeTab === 'demographics' && <DemographicsTab researchData={displayData} />}
        {activeTab === 'competitor' && <CompetitorTab researchData={displayData} />}
        {activeTab === 'reviews' && <ReviewsTab researchData={displayData} />}
        {activeTab === 'discovery' && <DiscoveryTab researchData={displayData} />}
        {activeTab === 'keywords' && <KeywordsTab researchData={displayData} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

const OverviewTab: React.FC<{ researchData?: any }> = ({ researchData }) => {
  // Access data from the correct backend structure - prioritize backend format
  const overviewData = researchData?.reports?.overview?.content || researchData?.overview;
  
  // Enhanced data access for missing fields
  const suggestions = overviewData?.top_suggestions || overviewData?.suggestions || [];
  const keyInsights = overviewData?.key_insights || [];
  // const strengths = overviewData?.strengths || [];
  // const weaknesses = overviewData?.weaknesses || [];
  const purchaseTriggers = overviewData?.purchase_triggers || [];
  const lovedFeatures = overviewData?.loved_features || [];
  const keywordStrategy = overviewData?.keyword_strategy || [];
  
  // Debug logging to see what data we're working with
  console.log('Research Dashboard Data:', {
    hasResearchData: !!researchData,
    hasOverviewData: !!overviewData,
    overviewDataKeys: overviewData ? Object.keys(overviewData) : [],
    keyInsightsExists: overviewData?.key_insights ? true : false,
    keyInsightsLength: overviewData?.key_insights?.length || 0,
    rawOverviewData: overviewData,
    keyInsightsData: overviewData?.key_insights,
    // Add detailed key analysis
    allKeys: overviewData ? Object.keys(overviewData) : [],
    hasKeyInsights: 'key_insights' in (overviewData || {}),
    hasKeyInsightsAlt: 'keyInsights' in (overviewData || {}),
    hasKeyInsightsUnderscore: 'key_insights' in (overviewData || {}),
    hasKeyInsightsCamel: 'keyInsights' in (overviewData || {}),
    // Backend structure analysis
    hasReports: !!researchData?.reports,
    reportsKeys: researchData?.reports ? Object.keys(researchData.reports) : [],
    overviewReport: researchData?.reports?.overview,
    overviewContent: researchData?.reports?.overview?.content
  });
  
  return (
  <div className="space-y-8">
    {/* Top Suggestions - First Section */}
    <div>
      <div style={{
        borderRadius: '20px',
        border: '1px solid #E2D5FF',
        background: '#F4F3FF',
        padding: '24px',
        gap: '32px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Top Suggestions To Improve Your Product</h3>
      </div>
        <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
      <div className="grid gap-6" style={{ gridTemplateColumns: '1fr' }}>
        {suggestions && suggestions.length > 0 ? (
          suggestions.map((suggestion: any, index: number) => (
            <SuggestionCard
              key={index}
              title={suggestion.suggestion || 'Improve Product Positioning'}
              description={suggestion.implementation || 'Implementation details not available'}
              percentage={`${Math.round(suggestion.benefit_percentage || 70)}%`}
              color="purple"
              tags={[]}
              impact={suggestion.priority || 'High'}
              impactText={suggestion.impact || 'High impact'}
            />
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading suggestions from backend...</p>
            <p className="text-xs text-gray-400 mt-2">Available data: {overviewData ? Object.keys(overviewData).join(', ') : 'None'}</p>
          </div>
        )}
        </div>
      </div>
    </div>

    {/* Key Insights Section */}
    <div>
      <div style={{
        borderRadius: '20px',
        border: '1px solid #E2D5FF',
        background: '#F4F3FF',
        padding: '24px',
        gap: '32px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Key Insights</h3>
      </div>
        <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
      <div className="grid gap-6" style={{ gridTemplateColumns: '1fr' }}>
                {keyInsights && keyInsights.length > 0 ? (
          keyInsights.map((insight: any, index: number) => (
              <SuggestionCard
                key={index}
                title={insight.insight || 'Key Insight'}
                description={insight.strategic_importance || 'Strategic insight from customer data'}
                percentage={`${Math.round(75 + (index * 10))}%`}
                color="green"
                tags={insight.content_placement || ['Strategic']}
                impact="High"
              />
            ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No key insights available for this project</p>
            <p className="text-xs text-gray-400 mt-2">Key insights will be generated when research reports are created</p>
          </div>
        )}
        </div>
      </div>
    </div>



    {/* Progress Sections */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" style={{ backgroundColor: '#F4F3FF', padding: '24px', borderRadius: '20px', border: '1px solid #E2D5FF' }}>
      <div style={{
        borderRadius: '20px',
        border: '1px solid #E2D5FF',
        background: '#ffffff',
        padding: '24px',
        gap: '32px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="flex items-center space-x-2">
          <CustomUserIcon className="h-5 w-5 text-gray-600" />
          <h4 className="text-lg font-semibold text-gray-900" style={{ fontSize: '24px', color: '#4A3B8E' }}>Reason Why People Buy</h4>
        </div>
        <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
        <div className="space-y-6">
                  {purchaseTriggers && purchaseTriggers.length > 0 ? (
          purchaseTriggers.map((trigger: any, index: number) => (
              <div key={index} style={{ marginBottom: '32px' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{trigger.trigger || 'Purchase Trigger'}</span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    trigger.driver_type === 'Functional' ? 'bg-green-100 text-green-700' :
                    trigger.driver_type === 'Emotional' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {trigger.driver_type || 'Functional'}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="w-full h-2" style={{ borderRadius: '8px', background: '#E0DEF6' }}>
                      <div 
                        className="h-2 transition-all duration-300" 
                        style={{ 
                                  width: `${Math.round(70 + (index * 10))}%`, 
                          borderRadius: '8px', 
                                  background: trigger.driver_type === 'Functional' ? '#10B981' : 
                                             trigger.driver_type === 'Emotional' ? '#F59E0B' : '#3D348B'
                        }}
                      />
                    </div>
                  </div>
                          <span className="text-sm font-medium text-gray-900">{Math.round(70 + (index * 10))}%</span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading purchase triggers from backend...</p>
            </div>
          )}
        </div>
      </div>

      <div style={{
        borderRadius: '20px',
        border: '1px solid #E2D5FF',
        background: '#FFF',
        padding: '24px',
        gap: '32px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="flex items-center space-x-2">
          <CustomUserIcon className="h-5 w-5 text-gray-600" />
          <h4 className="text-lg font-semibold text-gray-900" style={{ fontSize: '24px', color: '#4A3B8E' }}>What Features Buyers Like The Most</h4>
        </div>
        <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
        <div className="space-y-6">
                  {lovedFeatures && lovedFeatures.length > 0 ? (
          lovedFeatures.map((feature: any, index: number) => (
              <div key={index} style={{ marginBottom: '32px' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{feature.feature || 'Loved Feature'}</span>
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                    Loved
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="w-full h-2" style={{ borderRadius: '8px', background: '#E0DEF6' }}>
                      <div 
                        className="h-2 transition-all duration-300" 
                        style={{ 
                                  width: `${Math.round(85 + (index * 5))}%`, 
                          borderRadius: '8px', 
                          background: '#10B981'
                        }}
                      />
                    </div>
                  </div>
                          <span className="text-sm font-medium text-gray-900">{Math.round(85 + (index * 5))}%</span>
                </div>
                        <p className="text-xs text-gray-500 mt-1">{feature.customer_evidence || 'Customer evidence'}</p>
                        {feature.visualization_recommendation && (
                          <p className="text-xs text-gray-400 mt-1">Recommendation: {feature.visualization_recommendation}</p>
                        )}
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading loved features from backend...</p>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Keyword Table */}
    <div style={{
      borderRadius: '20px',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h4 className="text-lg font-semibold text-gray-900" style={{ fontSize: '24px', color: '#4A3B8E' }}>Optimized Keyword Usage</h4>
      </div>
      <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
      <div className="overflow-x-auto" style={{ border: '1px solid #C8B4F2', borderRadius: '12px' }}>
        <table className="min-w-full" style={{ borderRadius: '12px', minWidth: '1200px' }}>
          <thead>
            <tr style={{ backgroundColor: '#9270DC', color: '#fff' }}>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">#</th>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">Target Keyword</th>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">Ideal Placement</th>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">Content Guidance</th>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">Search Intent</th>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">Action</th>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">Rationale</th>
              <th className="text-left py-4 px-4 font-semibold text-white text-sm whitespace-nowrap">Expected Impact</th>
            </tr>
          </thead>
          <tbody style={{ background: 'white' }}>
                    {keywordStrategy && keywordStrategy.length > 0 ? (
          keywordStrategy.map((keyword: any, index: number) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={{ 
                  borderBottom: index === overviewData.keyword_strategy.length - 1 ? 'none' : '1px solid #C8B4F2', 
                  backgroundColor: '#FAF8FF' 
                }}>
                  <td className="py-4 px-4 text-sm font-medium text-gray-900 whitespace-nowrap">{(index + 1).toString().padStart(2, '0')}</td>
                  <td className="py-4 px-4 text-sm text-gray-900 font-medium whitespace-nowrap">{keyword.target_keyword || 'Target Keyword'}</td>
                  <td className="py-4 px-4 text-sm text-gray-700 whitespace-nowrap">{keyword.ideal_placement || 'Title, Bullet points, A+ content'}</td>
                  <td className="py-4 px-4 text-sm text-gray-700 max-w-xs">
                    <div className="truncate" title={keyword.content_guidance || 'Content guidance for keyword usage'}>
                      {keyword.content_guidance || 'Use in product descriptions and marketing materials'}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-700 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      keyword.search_intent === 'Informational' ? 'bg-blue-100 text-blue-700' :
                      keyword.search_intent === 'Transactional' ? 'bg-green-100 text-green-700' :
                      keyword.search_intent === 'Navigational' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {keyword.search_intent || 'Informational'}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-700 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      keyword.action === 'add' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {keyword.action === 'add' ? 'Add' : 'Replace'}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-700 max-w-xs">
                    <div className="truncate" title={keyword.rationale || 'Keyword reasoning'}>
                      {keyword.rationale || 'Keyword reasoning'}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-700 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      keyword.expected_impact === 'High' ? 'bg-green-100 text-green-700' :
                      keyword.expected_impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      keyword.expected_impact === 'Low' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {keyword.expected_impact || 'Medium'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-500">
                  Loading keyword strategy from backend...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
  );
};

const DemographicsTab: React.FC<{ researchData?: any }> = ({ researchData }) => {
  // Access data from the correct backend structure - prioritize backend format
  const demographicsData = researchData?.reports?.demographics?.content || researchData?.demographics;
  
  // Enhanced data access for missing fields with proper fallbacks
  const customerSegments = demographicsData?.customer_segments || [];
  // const physicalEnvironment = demographicsData?.physical_environment || {};
  const usageScenarios = demographicsData?.usage_scenarios || [];
  const purchaseDrivers = demographicsData?.purchase_drivers || [];
  const concernsObjections = demographicsData?.concerns_objections || [];
  const coreValues = demographicsData?.core_values || [];
  
  // Debug logging to see what data we're working with
  console.log('Demographics Data:', {
    hasResearchData: !!researchData,
    hasDemographicsData: !!demographicsData,
    demographicsDataKeys: demographicsData ? Object.keys(demographicsData) : [],
    customerSegments: demographicsData?.customer_segments,
    rawDemographicsData: demographicsData,
    // Backend structure analysis
    hasReports: !!researchData?.reports,
    reportsKeys: researchData?.reports ? Object.keys(researchData.reports) : [],
    demographicsReport: researchData?.reports?.demographics,
    demographicsContent: researchData?.reports?.demographics?.content
  });
  
  // Local state for Customer Psychology tabs
  const [activePsychTab, setActivePsychTab] = useState<'purchase' | 'concerns' | 'values'>('concerns');
  
  return (
    <div className="space-y-8">
      {/* Customer Profile Section */}
      <div style={{
        gap: '32px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="flex items-center space-x-2">
          <CustomUserIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-xl font-semibold text-gray-900" style={{
            color: '#4A3B8E',
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 'normal',
            textTransform: 'capitalize'
          }}>Customer Profile</h3>
        </div>
        
        {/* Left Column - Gender and Age */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Stacked Cards */}
          <div className="space-y-4">
            {/* Gender Section */}
            <div style={{ 
              backgroundColor: '#F5f5f5', 
              padding: '16px', 
              borderRadius: '12px',
              border: '1px solid #E2D5FF'
            }}>
              <h4 className="text-lg font-semibold mb-4" style={{ 
                color: '#333333',
                fontSize: '18px',
                fontWeight: 600
              }}>Gender</h4>
              <div className="flex items-center justify-between">
                {/* Dynamic Pie Chart */}
                <div className="relative" style={{ width: '240px', height: '240px' }}>
                  {(() => {
                    // Helpers
                    const parsePercent = (v: any): number => {
                      if (v == null) return 0;
                      if (typeof v === 'string') {
                        const cleaned = v.replace(/[^0-9.\-]/g, '');
                        const n = Number(cleaned);
                        if (isNaN(n)) return 0;
                        return n > 1 ? n : n * 100;
                      }
                      const n = Number(v);
                      return isNaN(n) ? 0 : (n > 1 ? n : n * 100);
                    };
                    const normGenderKey = (k: string): string => {
                      const s = (k || '').toString().toLowerCase();
                      if (/^(male|men|m)$/i.test(s)) return 'Male';
                      if (/^(female|women|f)$/i.test(s)) return 'Female';
                      return 'Other';
                    };

                    // Aggregate gender distribution from backend customer_segments
                    const accumulator: Record<string, number> = {};
                    if (Array.isArray(customerSegments) && customerSegments.length > 0) {
                      customerSegments.forEach((segment: any) => {
                        const segPct = parsePercent(segment?.percentage);
                        const distObj = segment?.gender_distribution;
                        if (Array.isArray(distObj)) {
                          distObj.forEach((entry: any) => {
                            const key = normGenderKey(entry?.gender ?? entry?.name ?? 'Other');
                            const gPct = parsePercent(entry?.percentage ?? entry?.value);
                            accumulator[key] = (accumulator[key] || 0) + (gPct / 100) * segPct;
                          });
                        } else if (distObj && typeof distObj === 'object') {
                          Object.entries(distObj).forEach(([gender, val]) => {
                            const key = normGenderKey(gender);
                            const gPct = parsePercent(val);
                            accumulator[key] = (accumulator[key] || 0) + (gPct / 100) * segPct;
                          });
                        }
                      });
                    }

                    // Fallback to top-level demographics content if segments missing
                    if (Object.keys(accumulator).length === 0) {
                      const top = (demographicsData as any)?.gender_distribution;
                      if (top) {
                        const entries = Array.isArray(top) ? top : Object.entries(top);
                        (entries as any[]).forEach((e: any) => {
                          const key = Array.isArray(e) ? normGenderKey(e[0]) : normGenderKey(e?.gender ?? e?.name ?? 'Other');
                          const val = Array.isArray(e) ? e[1] : (e?.percentage ?? e?.value);
                          accumulator[key] = (accumulator[key] || 0) + parsePercent(val);
                        });
                      }
                    }

                    const sumAgg = Object.values(accumulator).reduce((s, v) => s + v, 0);
                    if (sumAgg <= 0) {
                      return (
                        <div className="flex items-center justify-center w-full h-full text-xs text-gray-500">No gender data available</div>
                      );
                    }

                    const genderData = Object.fromEntries(
                      Object.entries(accumulator).map(([g, v]) => [g, Math.round((v / sumAgg) * 100)])
                    ) as Record<string, number>;

                    const total = Object.values(genderData).reduce((sum, val) => sum + val, 0);
                    let currentAngle = 0;
                    const colors = ['#4A3B8E', '#7F60BF', '#AA96DC'];

                    return (
                      <svg width="240" height="240" viewBox="0 0 100 100">
                        {Object.entries(genderData).map(([gender, percentage], index) => {
                          if (!percentage) return null;
                          const angle = (percentage / total) * 360;
                          const x1 = 50 + 40 * Math.cos(currentAngle * Math.PI / 180);
                          const y1 = 50 + 40 * Math.sin(currentAngle * Math.PI / 180);
                          const x2 = 50 + 40 * Math.cos((currentAngle + angle) * Math.PI / 180);
                          const y2 = 50 + 40 * Math.sin((currentAngle + angle) * Math.PI / 180);
                          const largeArcFlag = angle > 180 ? 1 : 0;
                          const pathData = [
                            `M 50 50`,
                            `L ${x1} ${y1}`,
                            `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                            'Z'
                          ].join(' ');
                          currentAngle += angle;
                          return (
                            <path
                              key={gender}
                              d={pathData}
                              fill={colors[index % colors.length]}
                            />
                          );
                        })}
                      </svg>
                    );
                  })()}
                </div>
                
                {/* Dynamic Legend */}
                <div className="space-y-3 flex-1 ml-8">
                  {(() => {
                    // Legend: aggregate and normalize gender distribution
                    const accumulator: Record<string, number> = {};
                    if (Array.isArray(demographicsData?.customer_segments)) {
                      demographicsData.customer_segments.forEach((segment: any) => {
                        const segPctRaw = Number(segment?.percentage ?? 0);
                        const segPct = isNaN(segPctRaw) ? 0 : (segPctRaw > 1 ? segPctRaw : segPctRaw * 100);
                        const dist = segment?.gender_distribution || {};
                        Object.entries(dist).forEach(([gender, val]) => {
                          const gRaw = Number(val as number);
                          const gPct = isNaN(gRaw) ? 0 : (gRaw > 1 ? gRaw : gRaw * 100);
                          accumulator[gender] = (accumulator[gender] || 0) + (gPct / 100) * segPct;
                        });
                      });
                    }

                    const sumAgg = Object.values(accumulator).reduce((s, v) => s + v, 0);
                    if (sumAgg <= 0) {
                      return (
                        <div className="text-xs text-gray-500">No gender data available</div>
                      );
                    }

                    const genderData = Object.fromEntries(
                      Object.entries(accumulator).map(([g, v]) => [g, Math.round((v / sumAgg) * 100)])
                    ) as Record<string, number>;

                    const colors = ['#4A3B8E', '#7F60BF', '#AA96DC'];
                    return (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#3B82F6' }}></div>
                            <span style={{ 
                              color: '#8C97A7',
                              fontSize: '10px',
                              fontWeight: 400,
                              lineHeight: '12px'
                            }}>Total</span>
                          </div>
                          <span style={{ 
                            color: '#2A2E33',
                            fontSize: '12px',
                            fontWeight: 600,
                            lineHeight: '12px'
                          }}>100%</span>
                        </div>
                        {Object.entries(genderData).map(([gender, percentage], index) => (
                          <div key={gender} className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors[index % colors.length] }}></div>
                              <span style={{ 
                                color: '#8C97A7',
                                fontSize: '10px',
                                fontWeight: 400,
                                lineHeight: '12px'
                              }}>{gender}</span>
                            </div>
                            <span style={{ 
                              color: '#2A2E33',
                              fontSize: '12px',
                              fontWeight: 600,
                              lineHeight: '12px'
                            }}>{percentage}%</span>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Age Section */}
            {(() => {
              // Helpers
              const parsePercent = (v: any): number => {
                if (v == null) return 0;
                if (typeof v === 'string') {
                  const cleaned = v.replace(/[^0-9.\-]/g, '');
                  const n = Number(cleaned);
                  if (isNaN(n)) return 0;
                  return n > 1 ? n : n * 100;
                }
                const n = Number(v);
                return isNaN(n) ? 0 : (n > 1 ? n : n * 100);
              };

              // Age distribution aggregated and normalized from backend structure(s)
              const accumulator: { [key: string]: number } = {};
              if (Array.isArray(customerSegments) && customerSegments.length > 0) {
                customerSegments.forEach((segment: any) => {
                  const segPct = parsePercent(segment?.percentage);
                  // Accept either age_range or nested distribution arrays/objects
                  if (segment?.age_distribution && Array.isArray(segment.age_distribution)) {
                    segment.age_distribution.forEach((entry: any) => {
                      const key = String(entry?.range || entry?.name || entry?.age_range || 'Other');
                      const pct = parsePercent(entry?.percentage ?? entry?.value);
                      accumulator[key] = (accumulator[key] || 0) + (pct / 100) * segPct;
                    });
                  } else if (segment?.age_distribution && typeof segment.age_distribution === 'object') {
                    Object.entries(segment.age_distribution).forEach(([range, val]) => {
                      const key = String(range);
                      const pct = parsePercent(val);
                      accumulator[key] = (accumulator[key] || 0) + (pct / 100) * segPct;
                    });
                  } else if (segment?.age_range) {
                    accumulator[segment.age_range] = (accumulator[segment.age_range] || 0) + segPct;
                  }
                });
              }

              // Fallback to top-level summary if segments missing
              if (Object.keys(accumulator).length === 0) {
                const top = (demographicsData as any)?.age_distribution;
                if (top) {
                  const entries = Array.isArray(top) ? top : Object.entries(top);
                  (entries as any[]).forEach((e: any) => {
                    const key = Array.isArray(e) ? String(e[0]) : String(e?.range || e?.name || e?.age_range || 'Other');
                    const val = Array.isArray(e) ? e[1] : (e?.percentage ?? e?.value);
                    accumulator[key] = (accumulator[key] || 0) + parsePercent(val);
                  });
                }
              }

              const sumAgg = Object.values(accumulator).reduce((s, v) => s + v, 0);
              const normalizedAgeData: { [key: string]: number } = sumAgg > 0
                ? Object.fromEntries(Object.entries(accumulator).map(([k, v]) => [k, Math.round((v / sumAgg) * 100)]))
                : {};

              // Calculate dynamic height based on number of age ranges
              const ageRangesCount = Object.keys(normalizedAgeData).length || 1;
              const barHeight = 28; // Height of each bar row (20px for bar + 8px margin)
              const chartHeight = ageRangesCount * barHeight;
              const cardHeight = 80 + chartHeight; // 80px for header + padding, chartHeight for bars
              
              return (
                <div style={{ 
                  backgroundColor: '#F8F8FA', 
                  padding: '20px', 
                  borderRadius: '12px',
                  border: '1px solid #E2D5FF',
                  height: `${cardHeight}px`
                }}>
                  <h4 className="text-lg font-semibold mb-3" style={{ 
                    color: '#333333',
                    fontSize: '18px',
                    fontWeight: 600
                  }}>Age</h4>
                  <div className="flex items-end space-x-6" style={{ height: `${chartHeight}px` }}>
                    
                    {/* Dynamic Bar Chart */}
                    <div className="flex items-end space-x-4 relative flex-1" style={{ height: `${chartHeight}px` }}>
                      {/* Grid lines (vertical for X-axis 0% start) */}
                      <div className="absolute inset-0 flex justify-between">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <div key={i} className="h-full w-px" style={{ backgroundColor: '#E6E6E6' }}></div>
                        ))}
                      </div>
                      
                      {/* Dynamic Bars - horizontal from 0% on X-axis */}
                      <div className="flex flex-col justify-between relative z-10 w-full" style={{ height: `${chartHeight}px` }}>
                        {(() => {
                          const colors = ['#4A3B8E', '#7F60BF', '#AA96DC', '#F59E0B', '#EF4444'];
                          const entries = Object.entries(normalizedAgeData);
                          if (entries.length === 0) {
                            return (
                              <div className="flex items-center justify-center w-full text-xs text-gray-500">No age data available</div>
                            );
                          }
                          return entries.map(([ageRange, percentage], index) => {
                            const widthPct = Math.max(0, Math.min(percentage, 100));
                            return (
                              <div key={ageRange} className="flex items-center space-x-3 mb-2">
                                <span className="w-16 text-xs text-gray-600">{ageRange}</span>
                                <div className="flex-1 bg-gray-200/50 rounded h-4 relative">
                                  <div 
                                    className="h-4 rounded transition-all duration-300" 
                                    style={{ width: `${widthPct}%`, backgroundColor: colors[index % colors.length] }}
                                  ></div>
                                </div>
                                <span className="w-10 text-xs font-semibold text-gray-900">{percentage}%</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Right Column - Physical Environment */}
          <div style={{
            backgroundColor: '#F5F5F5', 
            padding: '16px', 
            borderRadius: '8px',
            border: '1px solid #E2D5FF'
          }}>
            {/* Header with Title and Dropdown */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold" style={{ 
                color: '#333333',
                fontSize: '18px',
                fontWeight: 600
              }}>Geographical Locations</h4>
              <div className="relative">
                <select className="px-3 py-1 border border-gray-300 rounded text-sm appearance-none bg-white pr-8" style={{ 
                  color: '#AAAAAA',
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB'
                }}>
                  <option>Location</option>
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M7 10l5 5 5-5" stroke="#AAAAAA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Geographical Distribution Section */}
            <div className="w-full mb-4" style={{ 
              minHeight: '120px'
            }}>
             
              <div className="space-y-2">
                {(() => {
                  // Extract city tier and income level data from customer segments
                  const geographicalData: { [key: string]: { city_tier: string; income_level: string; percentage: number } } = {};
                  
                  if (demographicsData?.customer_segments) {
                    demographicsData.customer_segments.forEach((segment: any) => {
                      if (segment.city_tier && segment.income_level && segment.percentage != null) {
                        const key = `${segment.city_tier} - ${segment.income_level}`;
                        // Convert to 0–100 once (handles 0–1 or 0–100 inputs)
                        const raw = Number(segment.percentage);
                        const pct = isNaN(raw) ? 0 : (raw > 1 ? raw : raw * 100);
                        if (geographicalData[key]) {
                          geographicalData[key].percentage += Math.round(pct);
                        } else {
                          geographicalData[key] = {
                            city_tier: segment.city_tier,
                            income_level: segment.income_level,
                            percentage: Math.round(pct)
                          };
                        }
                      }
                    });
                  }
                  
                  // If no data, show sample data
                  if (Object.keys(geographicalData).length === 0) {
                    geographicalData['Urban - Medium to high'] = {
                      city_tier: 'Urban',
                      income_level: 'Medium to high',
                      percentage: 55
                    };
                    geographicalData['Suburban - Medium to high'] = {
                      city_tier: 'Suburban',
                      income_level: 'Medium to high',
                      percentage: 45
                    };
                  }
                  
                  const locationColors = ['#4A3B8E', '#7F60BF', '#AA96DC', '#F59E0B', '#EF4444'];
                  
                  return Object.entries(geographicalData).map(([, data], index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 rounded-lg" style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 rounded-full" style={{ 
                          backgroundColor: locationColors[index % locationColors.length] 
                        }}></div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium" style={{
                            color: '#333333',
                            fontSize: '14px',
                            fontWeight: 500
                          }}>
                            {data.city_tier}
                          </span>
                          <span className="text-xs" style={{
                            color: '#4A3B8E',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            Income: {data.income_level}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-semibold" style={{
                          color: '#4A3B8E',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          {data.percentage}%
                        </span>
                        <div className="w-2 h-2 rounded-full" style={{ 
                          backgroundColor: locationColors[index % locationColors.length] 
                        }}></div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            
            {/* Physical Environment Table */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', padding: '16px' }}>
              <h5 className="text-md font-semibold mb-3" style={{
                color: '#333333',
                fontSize: '16px',
                fontWeight: 600
              }}>Physical Environment</h5>
              <div className="space-y-3">
                {(() => {
                  // Get primary locations from demographics data
                  const primaryLocations = demographicsData?.physical_environment?.primary_locations || ['home', 'gym', 'office'];
                  
                  const locationData = primaryLocations.map((location: string, index: number) => {
                    const colors = ['#3498DB', '#2ECC71', '#9B59B6', '#F39C12', '#E74C3C'];
                    const bgColors = ['#D4EEF9', '#D4F9E0', '#EED4F9', '#F9EED4', '#F9D4D4'];
                    const popularity = 100 - (index * 20); // Simulate popularity based on order
                    const sales = Math.round(popularity * 0.6); // Simulate sales based on popularity
                    
                    return {
                      id: String(index + 1).padStart(2, '0'),
                      name: location.charAt(0).toUpperCase() + location.slice(1), // Capitalize first letter
                      popularity: Math.max(popularity, 20),
                      sales: Math.max(sales, 10),
                      color: colors[index % colors.length],
                      bgColor: bgColors[index % bgColors.length]
                    };
                  });
                  
                  return locationData.map((env: any, index: number) => (
                    <div key={index} className="flex items-center justify-between py-3" style={{
                      borderBottom: index === locationData.length - 1 ? 'none' : '1px solid #E5E7EB'
                    }}>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium" style={{
                          color: '#AAAAAA',
                          fontSize: '14px',
                          fontWeight: 500,
                          minWidth: '20px'
                        }}>{env.id}</span>
                        <span className="text-sm" style={{
                          color: '#333333',
                          fontSize: '14px',
                          fontWeight: 400
                        }}>{env.name}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-24 h-2" style={{
                          borderRadius: '4px',
                          background: '#F3F4F6',
                          overflow: 'hidden'
                        }}>
                          <div
                            className="h-2 transition-all duration-300"
                            style={{
                              width: `${env.popularity}%`,
                              borderRadius: '4px',
                              background: env.color
                            }}
                          />
                        </div>
                        <div className="px-3 py-1 rounded-full flex items-center justify-center text-xs font-medium" style={{
                          backgroundColor: env.bgColor,
                          color: env.color,
                          minWidth: '32px',
                          height: '24px'
                        }}>
                          {env.sales}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Behavior Section */}
    <div style={{
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Customer Behavior</h3>
      </div>
      
      <div className="grid gap-6" style={{ gridTemplateColumns: '1fr', backgroundColor: '#F5F5F5', borderRadius: '12px', padding: '24px' }}>
                    {(usageScenarios || []).map((item: any, index: number) => (
          <div key={index} className="flex items-center space-x-4 p-4" style={{ backgroundColor: '#FFFFFF', borderRadius: '12px' }}>
            <div className="flex-shrink-0">
              <ProgressCircle 
                percentage={`${Math.round(item.percentage || 0)}%`} 
                color="purple" 
                size="sm" 
              />
            </div>
            <div className="flex-1">
              <p className="text-sm" style={{ color: '#4A3B8E' }}>
                {item.scenario}{item.context ? ` - ${item.context}` : ''}
              </p>
              {item.key_reason && (
                <p className="text-xs mt-1" style={{ color: '#6B6B6B' }}>
                  Reason: {item.key_reason}
                </p>
              )}
            </div>
          </div>
        ))}
                    {(!usageScenarios || usageScenarios.length === 0) && (
          <div className="text-center text-gray-500">Loading usage scenarios from backend...</div>
        )}
      </div>
    </div>

     {/* Customer Psychology Section */}
    <div style={{
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Customer Psychology</h3>
      </div>
      
      {/* Tabs */}
      <div className="flex space-x-2">
        <button onClick={() => setActivePsychTab('purchase')} className="text-sm font-medium rounded-lg" style={{ 
          display: 'flex',
          width: '172px',
          height: '48px',
          padding: '16px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          borderRadius: '12px',
          border: activePsychTab === 'purchase' ? 'none' : '1px solid #3D348B',
          color: activePsychTab === 'purchase' ? '#FFFFFF' : '#4A3B8E', 
          background: activePsychTab === 'purchase' ? 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)' : 'transparent'
        }}>
          Purchase Trigger
        </button>
        <button onClick={() => setActivePsychTab('concerns')} className="text-sm font-medium rounded-lg" style={{ 
          display: 'flex',
          width: '172px',
          height: '48px',
          padding: '16px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          borderRadius: '12px',
          border: activePsychTab === 'concerns' ? 'none' : '1px solid #3D348B',
          color: activePsychTab === 'concerns' ? '#FFFFFF' : '#4A3B8E', 
          background: activePsychTab === 'concerns' ? 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)' : 'transparent'
        }}>
          Biggest Concern
        </button>
        <button onClick={() => setActivePsychTab('values')} className="text-sm font-medium rounded-lg" style={{ 
          display: 'flex',
          width: '172px',
          height: '48px',
          padding: '16px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          borderRadius: '12px',
          border: activePsychTab === 'values' ? 'none' : '1px solid #3D348B',
          color: activePsychTab === 'values' ? '#FFFFFF' : '#4A3B8E', 
          background: activePsychTab === 'values' ? 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)' : 'transparent'
        }}>
          Core Value
        </button>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto" style={{ border: '1px solid #E2D5FF', borderRadius: '12px' }}>
        <table className="min-w-full" style={{ borderRadius: '12px' }}>
          <thead>
            <tr style={{ backgroundColor: '#F3F1FF', color: '#3D348B' }}>
              <th className="text-left py-4 px-6 font-semibold text-sm">#</th>
              <th className="text-left py-4 px-6 font-semibold text-sm">{activePsychTab === 'purchase' ? 'Trigger event or pain point' : activePsychTab === 'concerns' ? 'Concern / Value' : 'Core Value'}</th>
              <th className="text-left py-4 px-6 font-semibold text-sm">{activePsychTab === 'purchase' ? 'Core motivation (What they want)' : activePsychTab === 'concerns' ? 'Reason / Importance' : 'Importance'}</th>
              <th className="text-left py-4 px-6 font-semibold text-sm">{activePsychTab === 'purchase' ? 'Type of driver' : activePsychTab === 'concerns' ? 'Aligned ICP' : 'Aligned ICP'}</th>
              <th className="text-left py-4 px-6 font-semibold text-sm">% of users</th>
            </tr>
          </thead>
          <tbody style={{ background: 'white' }}>
                          {(activePsychTab === 'purchase' ? (purchaseDrivers || [])
                : activePsychTab === 'concerns' ? (concernsObjections || [])
              : (coreValues || [])
            ).map((row: any, index: number) => (
              <tr key={index} style={{ 
                borderBottom: '1px solid #C8B4F2', 
                backgroundColor: '#FFFFFF' 
              }}>
                <td className="py-4 px-6 text-sm font-medium" style={{ color: '#4A3B8E' }}>{(index + 1).toString().padStart(2, '0')}</td>
                {activePsychTab === 'purchase' && (
                  <>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.trigger_event}</td>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.core_motivation}</td>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.driver_type}</td>
                  </>
                )}
                {activePsychTab === 'concerns' && (
                  <>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.concern}</td>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.reason}</td>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>—</td>
                  </>
                )}
                {activePsychTab === 'values' && (
                  <>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.value}</td>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.importance}</td>
                    <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>{row.aligned_icp}</td>
                  </>
                )}
                <td className="py-4 px-6 text-sm" style={{ color: '#4A3B8E' }}>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 h-2" style={{ borderRadius: '8px', background: '#E0DEF6' }}>
                      <div 
                        className="h-2 transition-all duration-300" 
                        style={{ 
                          width: `${Math.round(row.percentage || 0)}%`, 
                          borderRadius: '8px', 
                          background: '#8B5CF6' 
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#4A3B8E' }}>{Math.round(row.percentage || 0)}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {(!demographicsData || ((activePsychTab === 'purchase' ? purchaseDrivers : activePsychTab === 'concerns' ? concernsObjections : coreValues) || []).length === 0) && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-500">
                  Loading {activePsychTab === 'purchase' ? 'purchase triggers' : activePsychTab === 'concerns' ? 'biggest concerns' : 'core values'} from backend...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};

const CompetitorTab: React.FC<{ researchData?: any }> = ({ researchData }) => {
  const [competitorType, setCompetitorType] = useState<'direct' | 'indirect'>('direct');
  // Prefer transformed shape if present, else raw
  const competitorData: any = researchData?.reports?.competitor?.content || researchData?.competitor || {};
  const directCompetitors: any[] = Array.isArray(competitorData.direct_competitors) ? competitorData.direct_competitors : [];
  const indirectCompetitors: any[] = Array.isArray(competitorData.indirect_competitors) ? competitorData.indirect_competitors : [];
  
  // Debug logging to see what data we're working with
  console.log('Competitor Data:', {
    hasResearchData: !!researchData,
    hasCompetitorData: !!competitorData,
    competitorDataKeys: competitorData ? Object.keys(competitorData) : [],
    directCompetitorsCount: directCompetitors.length,
    indirectCompetitorsCount: indirectCompetitors.length,
    rawCompetitorData: competitorData,
    // Backend structure analysis
    hasReports: !!researchData?.reports,
    reportsKeys: researchData?.reports ? Object.keys(researchData.reports) : [],
    competitorReport: researchData?.reports?.competitor,
    competitorContent: researchData?.reports?.competitor?.content,
    // Detailed competitor analysis
    directCompetitorsPreview: directCompetitors.slice(0, 2).map(c => ({
      name: c?.name,
      tier: c?.tier,
      marketPositioning: c?.market_positioning,
      price: c?.price,
      hasReviewMetrics: !!c?.review_metrics,
      reviewMetricsKeys: c?.review_metrics ? Object.keys(c.review_metrics) : []
    })),
    // Competitor keyword analysis debug
    hasCompetitorKeywordAnalysis: !!competitorData?.competitor_keyword_analysis,
    competitorKeywordAnalysisKeys: competitorData?.competitor_keyword_analysis ? Object.keys(competitorData.competitor_keyword_analysis) : [],
    marketKeywordGaps: competitorData?.competitor_keyword_analysis?.market_keyword_gaps,
    competitorKeywordStrategies: competitorData?.competitor_keyword_analysis?.competitor_keyword_strategies,
    keywordOpportunities: competitorData?.competitor_keyword_analysis?.keyword_opportunities,
    keywordRankingAnalysis: competitorData?.competitor_keyword_analysis?.keyword_ranking_analysis,
    // Fallback keyword analysis debug
    hasKeywordAnalysis: !!competitorData?.keyword_analysis,
    keywordAnalysisKeys: competitorData?.keyword_analysis ? Object.keys(competitorData.keyword_analysis) : []
  });
  
  // Derived content for sections below (dynamic values only)
  const productAnalysis = competitorData.product_analysis || {};
  const differentiators = productAnalysis.differentiators || [];
  const firstDirect = directCompetitors?.[0] || {};
  const flavorVariants = firstDirect?.flavor_variants;
  const usageScenarios = firstDirect?.usage_scenarios
    || researchData?.reports?.demographics?.content?.usage_scenarios
    || researchData?.demographics?.usage_scenarios
    || [];
  
  // Prefer competitor->customer_sentiments (raw). Fallback to Reviews report (raw) or transformed store shape
  const reviewsReportRaw = researchData?.reports?.reviews?.content;
  const reviewsTransformed = researchData?.reviews;
  const positiveSentiments = competitorData.customer_sentiments?.positive_sentiments
    || reviewsReportRaw?.customer_sentiments?.positive_sentiments
    || (Array.isArray(reviewsTransformed?.positive_reviews)
      ? reviewsTransformed.positive_reviews.map((r: any) => ({ sentiment: r.sentiment, percentage: r.percentage }))
      : []);
  const negativeSentiments = competitorData.customer_sentiments?.negative_sentiments
    || reviewsReportRaw?.customer_sentiments?.negative_sentiments
    || (Array.isArray(reviewsTransformed?.negative_reviews)
      ? reviewsTransformed.negative_reviews.map((r: any) => ({ sentiment: r.sentiment, percentage: r.percentage }))
      : []);
  const totalReviews = competitorData.customer_sentiments?.total_reviews
    ?? reviewsReportRaw?.customer_sentiments?.total_reviews
    ?? reviewsTransformed?.total_reviews;
  const averageRating = competitorData.customer_sentiments?.average_rating
    ?? reviewsReportRaw?.customer_sentiments?.average_rating
    ?? reviewsTransformed?.average_rating;
  const barriers = competitorData.customer_sentiments?.barriers
    || reviewsReportRaw?.customer_sentiments?.barriers
    || [];
  
  return (
  <div className="space-y-8">
      {/* Competitor Landscape Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F4F3FF',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
          }}>Competitor Landscape</h3>
      </div>
      <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
      
        {/* Sub-tabs */}
        <div className="flex space-x-2">
          <button 
            className="px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200"
            style={{ 
              color: competitorType === 'direct' ? '#FFFFFF' : '#4A3B8E',
              backgroundColor: competitorType === 'direct' 
                ? '#4A3B8E'
                : '#E2D5FF'
            }}
            onClick={() => setCompetitorType('direct')}
          >
            Direct Competitor
          </button>
          <button 
            className="px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200"
            style={{ 
              color: competitorType === 'indirect' ? '#FFFFFF' : '#4A3B8E',
              backgroundColor: competitorType === 'indirect' 
                ? '#4A3B8E'
                : '#E2D5FF'
            }}
            onClick={() => setCompetitorType('indirect')}
          >
            Indirect Competitor
          </button>
              </div>
              
        {/* Competitor Table */}
        <div className="overflow-x-auto" style={{ border: '1px solid #E5E7EB', borderRadius: '12px', backgroundColor: '#FFFFFF' }}>
          <table className="min-w-full" style={{ borderRadius: '12px', backgroundColor: '#FFFFFF' }}>
            <thead>
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-sm" style={{ color: '#374151', width: '200px', backgroundColor: '#FFF' }}></th>
                {(competitorType === 'direct' ? ['Bestseller', 'Premium', 'Mid', 'Budget'] : ['Alternative 1', 'Alternative 2', 'Alternative 3', 'Alternative 4']).map((tier, index) => (
                  <th key={index} className="text-center py-4 px-6 font-semibold text-sm" style={{ color: '#4A3B8E', backgroundColor: '#E0DEF6' }}>
                    <span>{tier}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {competitorType === 'direct' ? (() => {
                // Order competitors into 4 columns by tier if available; otherwise first 4
                const tiers = ['Bestseller', 'Premium', 'Mid', 'Budget'];
                const hasAnyTier = directCompetitors.some((c) => !!(c?.tier || c?.market_positioning));
                let ordered: Array<any | undefined> = new Array(4).fill(undefined);
                
                // Debug logging for competitor tiering
                console.log('Competitor Tiering:', {
                  directCompetitorsCount: directCompetitors.length,
                  hasAnyTier,
                  competitors: directCompetitors.map(c => ({
                    name: c?.name,
                    tier: c?.tier,
                    market_positioning: c?.market_positioning
                  }))
                });
                
                if (hasAnyTier) {
                  // Map backend tiers to frontend tiers
                  const tierMapping: { [key: string]: number } = {
                    'market leader': 0, 'bestseller': 0, 'leader': 0,
                    'premium': 1, 'high-end': 1,
                    'mid': 2, 'mid-market': 2, 'mid market': 2,
                    'budget': 3, 'economy': 3, 'value': 3
                  };
                  
                  // Place each competitor into its matching tier column
                  directCompetitors.forEach((competitor) => {
                    const tierText = (competitor?.tier || competitor?.market_positioning || '').toLowerCase();
                    let tierIndex = -1;
                    
                    // Find matching tier
                    for (const [key, index] of Object.entries(tierMapping)) {
                      if (tierText.includes(key)) {
                        tierIndex = index;
                        break;
                      }
                    }
                    
                    // If no specific tier match, place in first available slot
                    if (tierIndex === -1) {
                      for (let i = 0; i < ordered.length; i++) {
                        if (!ordered[i]) {
                          tierIndex = i;
                          break;
                        }
                      }
                    }
                    
                                      // Place competitor in the appropriate slot
                  if (tierIndex >= 0 && tierIndex < ordered.length && !ordered[tierIndex]) {
                    ordered[tierIndex] = competitor;
                  }
                });
                
                // Fill any remaining empty slots with remaining competitors
                const remainingCompetitors = directCompetitors.filter(comp => !ordered.includes(comp));
                for (let i = 0; i < ordered.length && remainingCompetitors.length > 0; i++) {
                  if (!ordered[i]) {
                    ordered[i] = remainingCompetitors.shift();
                  }
                }
                
                console.log('Tier mapping debug:', ordered.map((c, i) => ({
                  tier: tiers[i],
                  competitor: c?.name,
                  originalTier: c?.tier,
                  marketPositioning: c?.market_positioning
                })));
                } else {
                  // No tiering info; place sequentially from left to right
                  for (let i = 0; i < Math.min(directCompetitors.length, 4); i++) {
                    ordered[i] = directCompetitors[i];
                  }
                }

                const rows = [
                  { label: 'Product Name', key: 'name' },
                  { label: 'ASIN', key: 'asin' },
                  { label: 'Price', key: 'price' },
                  { label: 'Quantity', key: 'quantity' },
                  { label: 'Rating', key: 'rating' },
                  { label: 'Reviews', key: 'reviews' },
                  { label: 'Headline Value Prop', key: 'headline_value_prop' },
                  { label: 'Market Positioning', key: 'market_positioning' },
                  { label: 'Target ICP', key: 'target_icp' },
                  { label: 'Repurchase Hooks', key: 'repurchase_hooks' },
                  { label: 'Functional Benefits', key: 'functional_benefits' },
                  { label: 'Formulation Elements', key: 'formulation_elements' },
                  { label: 'USPs', key: 'usps' },
                  { label: 'Drawbacks', key: 'drawbacks' },
                  { label: 'Flavor Variants', key: 'flavor_variants' },
                  { label: 'Positive Insights', key: 'positive_insights' },
                  { label: 'Recurring Problems', key: 'recurring_problems' },
                  { label: 'Purchase Reasons', key: 'purchase_reasons' },
                  { label: 'Usage Scenarios', key: 'usage_scenarios' }
                ];

                // Check if we have any competitors to display
                if (ordered.every(comp => !comp)) {
                  return (
                    <tr key="no-data">
                      <td colSpan={5} className="py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center space-y-2">
                          <AlertCircle className="h-8 w-8 text-gray-400" />
                          <p>No direct competitors found in the data</p>
                          <p className="text-sm text-gray-400">
                            Available competitors: {directCompetitors.length} | 
                            Tiers found: {directCompetitors.map(c => c?.tier || c?.market_positioning).filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return rows.map((row, rowIndex) => {
                  const greyBackgroundRows = ['Product Name', 'Price', 'Headline Value Prop', 'Target ICP'];
                  const shouldHaveGreyBackground = greyBackgroundRows.includes(row.label);
                  return (
                    <tr key={rowIndex} style={{ backgroundColor: shouldHaveGreyBackground ? '#F5F5F5' : '#FFFFFF' }}>
                      <td className="py-4 px-6 text-sm font-medium" style={{ color: '#374151', backgroundColor: shouldHaveGreyBackground ? '#F5F5F5' : '#FFFFFF', borderRight: '1px solid #E5E7EB' }}>
                        {row.label}
                      </td>
                      {ordered.map((comp, colIndex) => {
                        const value = comp ? (() => {
                          // Debug logging for individual competitor data
                          if (row.key === 'name') {
                            console.log('Competitor data debug:', {
                              competitorName: comp.name,
                              tier: comp.tier,
                              marketPositioning: comp.market_positioning,
                              price: comp.price,
                              reviewMetrics: comp.review_metrics,
                              allKeys: Object.keys(comp)
                            });
                          }
                          
                          switch (row.key) {
                            case 'rating':
                              return comp.review_metrics?.average_rating ?? comp.rating;
                            case 'reviews':
                              return comp.review_metrics?.total_reviews ?? comp.reviews;
                            case 'repurchase_hooks':
                              return Array.isArray(comp.repurchase_hooks) ? comp.repurchase_hooks.join(', ') : comp.repurchase_hooks;
                            case 'functional_benefits':
                              return Array.isArray(comp.functional_benefits)
                                ? comp.functional_benefits.map((b: any) => `${b.benefit || b}${b.percentage ? ` (${b.percentage}%)` : ''}`).join(' | ')
                                : comp.functional_benefits;
                            case 'formulation_elements':
                              return Array.isArray(comp.formulation_elements)
                                ? comp.formulation_elements.map((e: any) => `${e.element || e}${e.importance ? ` – ${e.importance}` : ''}`).join(' | ')
                                : comp.formulation_elements;
                            case 'usps':
                              return Array.isArray(comp.usps)
                                ? comp.usps.map((u: any) => `${u.usp || u}${u.percentage ? ` (${u.percentage}%)` : ''}`).join(' | ')
                                : comp.usps;
                            case 'drawbacks':
                              return Array.isArray(comp.drawbacks)
                                ? comp.drawbacks.map((d: any) => `${d.drawback || d}${d.percentage ? ` (${d.percentage}%)` : ''}`).join(' | ')
                                : comp.drawbacks;
                            case 'flavor_variants': {
                              const fv = comp.flavor_variants;
                              if (!fv) return undefined;
                              const missing = Array.isArray(fv.missing_options) ? fv.missing_options.join(', ') : fv.missing_options;
                              const parts = [
                                fv.total_count != null ? `${fv.total_count} variants` : undefined,
                                missing ? `Missing: ${missing}` : undefined,
                                fv.variety_seekers_percentage != null ? `Variety seekers: ${fv.variety_seekers_percentage}%` : undefined
                              ].filter(Boolean);
                              return parts.join(' | ');
                            }
                            case 'positive_insights':
                              return Array.isArray(comp.positive_insights)
                                ? comp.positive_insights.map((i: any) => `${i.insight || i}${i.percentage ? ` (${i.percentage}%)` : ''}`).join(' | ')
                                : comp.positive_insights;
                            case 'recurring_problems':
                              return Array.isArray(comp.recurring_problems)
                                ? comp.recurring_problems.map((p: any) => `${p.problem || p}${p.percentage ? ` (${p.percentage}%)` : ''}`).join(' | ')
                                : comp.recurring_problems;
                            case 'purchase_reasons':
                              return Array.isArray(comp.purchase_reasons)
                                ? comp.purchase_reasons.map((r: any) => `${r.reason || r}${r.percentage ? ` (${r.percentage}%)` : ''}`).join(' | ')
                                : comp.purchase_reasons;
                            case 'usage_scenarios':
                              return Array.isArray(comp.usage_scenarios)
                                ? comp.usage_scenarios.map((s: any) => `${s.scenario || s}${s.percentage ? ` (${s.percentage}%)` : ''}`).join(' | ')
                                : comp.usage_scenarios;
                            case 'price':
                              // Handle price object structure
                              if (comp.price && typeof comp.price === 'object') {
                                return `${comp.price.currency || '$'}${comp.price.value || comp.price}`;
                              }
                              return comp.price;
                            case 'quantity':
                              // Handle quantity display
                              return comp.quantity || 'N/A';
                            case 'asin':
                              // Extract ASIN from URL if needed
                              const asinValue = (comp as any)[row.key];
                              if (asinValue && asinValue.includes('amazon.com')) {
                                return extractASIN(asinValue) || asinValue;
                              }
                              return asinValue;
                            default:
                              return (comp as any)[row.key];
                          }
                        })() : undefined;
                        return (
                          <td key={colIndex} className="py-4 px-6 text-sm text-center" style={{ color: '#374151', borderRight: colIndex === 3 ? 'none' : '1px solid #E5E7EB' }}>
                              <div className="rounded-lg p-3 border border-gray-200 bg-gray-100 text-left">
                                {value ?? '—'}
          </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                });
              })() : (() => {
                // Indirect competitors: show up to 4 alternatives with core fields
                const cols: Array<any | undefined> = new Array(4).fill(undefined).map((_, i) => indirectCompetitors[i]);
                const rows = [
                  { label: 'Name', key: 'name' },
                  { label: 'Format', key: 'format' },
                  { label: 'Core Benefit', key: 'core_benefit' },
                  { label: 'Overlap', key: 'competition_overlap' }
                ];
                return rows.map((row, rowIndex) => (
                  <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                    <td className="py-4 px-6 text-sm font-medium" style={{ color: '#374151', borderRight: '1px solid #E5E7EB' }}>{row.label}</td>
                    {cols.map((comp, colIndex) => (
                      <td key={colIndex} className="py-4 px-6 text-sm text-center" style={{ color: '#374151', borderRight: colIndex === 3 ? 'none' : '1px solid #E5E7EB' }}>
                        <div className="rounded-lg p-3 border border-gray-200 bg-gray-100 text-left">
                          {comp ? (comp as any)[row.key] ?? '—' : '—'}
                        </div>
                      </td>
                    ))}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
      </div>
    </div>

      {/* Strengths & Weaknesses Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F4F3FF',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
          }}>Strengths & Weaknesses</h3>
      </div>
      <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
      
        {/* Strengths & Weaknesses Table with Horizontal Scroll */}
        <div className="overflow-x-auto" style={{ border: '1px solid #E5E7EB', borderRadius: '12px', backgroundColor: '#FFFFFF' }}>
          <table className="min-w-full" style={{ borderRadius: '12px', backgroundColor: '#FFFFFF', minWidth: '1200px' }}>
            <thead>
              <tr>
                {['Benefits', 'Feature Gaps or Complaints', 'Formulation Uniqueness (if applicable)', 'Flavor & Variants (if applicable)', 'Unique Edge/Innovation'].map((category, index) => (
                  <th key={index} className="text-left py-4 px-6 font-semibold text-sm" style={{ color: '#4A3B8E', backgroundColor: '#E0DEF6', minWidth: '300px' }}>
                    <span>{category}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const uniqBy = (arr: any[], text: (x: any) => string) => {
                  const map = new Map<string, any>();
                  arr.forEach((x) => {
                    const key = text(x).trim().toLowerCase();
                    if (key && !map.has(key)) map.set(key, x);
                  });
                  return Array.from(map.values());
                };

                const benefitItems = Array.isArray(firstDirect?.functional_benefits)
                  ? uniqBy(firstDirect.functional_benefits, (b: any) => String(b?.benefit ?? b)).slice(0, 4)
                  : [];
                const problemItems = Array.isArray(firstDirect?.recurring_problems)
                  ? uniqBy(firstDirect.recurring_problems, (p: any) => String(p?.problem ?? p)).slice(0, 5)
                  : [];
                const formulationItems = Array.isArray(firstDirect?.formulation_elements)
                  ? uniqBy(firstDirect.formulation_elements, (e: any) => String(e?.element ?? e)).slice(0, 3)
                  : [];
                const differentiatorItems = Array.isArray(differentiators)
                  ? uniqBy(differentiators, (d: any) => String(d?.differentiator ?? d)).slice(0, 3)
                  : [];

                return (
                  <tr style={{ backgroundColor: '#FFFFFF' }}>
                    {/* Benefits Column */}
                    <td className="py-4 px-6 text-sm text-left" style={{ 
                      color: '#374151', 
                      borderRight: '1px solid #E5E7EB',
                      verticalAlign: 'top',
                      minWidth: '350px',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <div className="space-y-2">
                        {benefitItems.length > 0 ? (
                          benefitItems.map((b: any, i: number) => (
                            <div key={i} className="flex items-start space-x-2">
                              <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: ['#3B82F6','#10B981','#F472B6','#F59E0B'][i % 4] }}></div>
                              <span className="text-sm font-normal">{`${b?.percentage ?? ''}${b?.percentage ? '% ' : ''}${b?.benefit ?? b}`}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">—</span>
                        )}
                      </div>
                    </td>

                    {/* Feature Gaps or Complaints Column */}
                    <td className="py-4 px-6 text-sm text-left" style={{ 
                      color: '#374151', 
                      borderRight: '1px solid #E5E7EB',
                      verticalAlign: 'top',
                      minWidth: '350px',
                      backgroundColor: '#FFFFFF',
                      textAlign: 'left'
                    }}>
                      <div className="space-y-2">
                        {problemItems.length > 0 ? (
                          problemItems.map((p: any, i: number) => (
                            <div key={i} className="text-sm font-normal">{`${p?.percentage ?? ''}${p?.percentage ? '% ' : ''}${p?.problem ?? p}`}</div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">—</span>
                        )}
                      </div>
                    </td>

                    {/* Formulation Uniqueness Column */}
                    <td className="py-4 px-6 text-sm" style={{ 
                      color: '#374151', 
                      borderRight: '1px solid #E5E7EB',
                      verticalAlign: 'top',
                      minWidth: '350px',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <div className="space-y-2">
                        {formulationItems.length > 0 ? (
                          formulationItems.map((e: any, i: number) => (
                            <div key={i} className="flex items-start space-x-2">
                              <div className="w-2 h-2 rounded-full mt-2 bg-purple-500"></div>
                              <span className="text-sm">{`${e?.element ?? e}${e?.importance ? ' – ' + e.importance : ''}`}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">—</span>
                        )}
                      </div>
                    </td>

                    {/* Flavor & Variants Column */}
                    <td className="py-4 px-6 text-sm" style={{ 
                      color: '#374151', 
                      borderRight: '1px solid #E5E7EB',
                      verticalAlign: 'top',
                      minWidth: '350px',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <div className="space-y-2">
                        {flavorVariants ? (
                          <>
                            <div className="flex items-start space-x-2">
                              <div className="w-2 h-2 rounded-full mt-2 bg-purple-500"></div>
                              <span className="text-sm">{flavorVariants.total_count != null ? `${flavorVariants.total_count} variants` : '—'}</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-2 h-2 rounded-full mt-2 bg-purple-500"></div>
                              <span className="text-sm">{Array.isArray(flavorVariants.missing_options) ? `Missing: ${flavorVariants.missing_options.join(', ')}` : '—'}</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-2 h-2 rounded-full mt-2 bg-purple-500"></div>
                              <span className="text-sm">{flavorVariants.variety_seekers_percentage != null ? `Variety seekers: ${flavorVariants.variety_seekers_percentage}%` : '—'}</span>
                            </div>
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">—</span>
                        )}
                      </div>
                    </td>

                    {/* Unique Edge/Innovation Column */}
                    <td className="py-4 px-6 text-sm" style={{ 
                      color: '#374151', 
                      borderRight: 'none',
                      verticalAlign: 'top',
                      minWidth: '350px',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <div className="space-y-2">
                        {differentiatorItems.length > 0 ? (
                          differentiatorItems.map((d: any, i: number) => (
                            <div key={i} className="flex items-start space-x-2">
                              <div className="w-2 h-2 rounded-full mt-2 bg-purple-500"></div>
                              <span className="text-sm">{d?.differentiator ?? d}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
      </div>
    </div>

      {/* Review Sentiments Section */}
    <div style={{
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
          }}>Review Sentiments</h3>
      </div>
        
        <div className="flex gap-8">
          {/* Review Metrics */}
          <div className="space-y-4 flex flex-col justify-center" style={{ width: '398px', height: '300px' }}>
            <div className="flex items-center p-4 rounded-lg flex-1" style={{ backgroundColor: '#F4F3FF', border: '1px solid #E2D5FF' }}>
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4">
                <BarChart3 className="h-5 w-5" style={{ color: '#4A3B8E' }} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium" style={{ color: '#4A3B8E' }}>Total Reviews</span>
                <span className="text-lg font-semibold" style={{ color: '#4A3B8E' }}>{totalReviews ?? '—'}</span>
              </div>
            </div>
            <div className="flex items-center p-4 rounded-lg flex-1" style={{ backgroundColor: '#F4F3FF', border: '1px solid #E2D5FF' }}>
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4">
                <BarChart3 className="h-5 w-5" style={{ color: '#4A3B8E' }} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium" style={{ color: '#4A3B8E' }}>Average Rating</span>
                <span className="text-lg font-semibold" style={{ color: '#4A3B8E' }}>{averageRating ? `${averageRating}/5` : '—'}</span>
              </div>
            </div>
            <div className="flex items-center p-4 rounded-lg flex-1" style={{ backgroundColor: '#F4F3FF', border: '1px solid #E2D5FF' }}>
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4">
                <BarChart3 className="h-5 w-5" style={{ color: '#4A3B8E' }} />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium" style={{ color: '#4A3B8E' }}>Top Positive</span>
                <span className="text-lg font-semibold" style={{ color: '#4A3B8E' }}>{positiveSentiments?.[0]?.sentiment ? `${positiveSentiments[0].percentage ?? ''}${positiveSentiments[0].percentage ? '% ' : ''}${positiveSentiments[0].sentiment}` : '—'}</span>
              </div>
            </div>
          </div>
          
          {/* Usage Insights */}
          <div className="rounded-lg p-4" style={{ backgroundColor: '#F4F3FF', border: '1px solid #E2D5FF', width: '690px', minHeight: '300px' }}>
            <h4 className="font-medium mb-4" style={{ color: '#000000', fontSize: '18px' }}>Usage Insights</h4>
      <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
            <div className="flex items-center space-x-6">
              <div className="relative" style={{ width: '250px', height: '200px' }}>
                <svg width="232" height="232" viewBox="0 0 100 100">
                  {(() => {
                    const data = (Array.isArray(usageScenarios) ? usageScenarios.slice(0, 4) : []) as any[];
                    const total = data.reduce((sum, u) => sum + (u?.percentage ?? 0), 0);
                    const colors = ['#93C5FD', '#FED7AA', '#FECACA', '#BBF7D0'];
                    const center = 50;
                    const radius = 40;
                    if (!data.length || total <= 0) {
                      return <circle cx={center} cy={center} r={radius} fill="#E0DEF6" />;
                    }
                    let currentAngle = 0;
                    return data.map((u, i) => {
                      const p = Math.max(0, u?.percentage ?? 0);
                      const angle = (p / total) * 360;
                      if (angle <= 0) return null;
                      const x1 = center + radius * Math.cos((currentAngle * Math.PI) / 180);
                      const y1 = center + radius * Math.sin((currentAngle * Math.PI) / 180);
                      const x2 = center + radius * Math.cos(((currentAngle + angle) * Math.PI) / 180);
                      const y2 = center + radius * Math.sin(((currentAngle + angle) * Math.PI) / 180);
                      const largeArcFlag = angle > 180 ? 1 : 0;
                      const d = [
                        `M ${center} ${center}`,
                        `L ${x1} ${y1}`,
                        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                        'Z',
                      ].join(' ');
                      currentAngle += angle;
                      return <path key={i} d={d} fill={colors[i % colors.length]} />;
                    });
                  })()}
                </svg>
              </div>
              <div className="space-y-3">
                {(Array.isArray(usageScenarios) && usageScenarios.length > 0 ? usageScenarios.slice(0, 4) : []).map((s: any, i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ['#93C5FD','#FED7AA','#FECACA','#BBF7D0'][i % 4] }}></div>
                      <span className="text-sm" style={{ color: '#6B7280' }}>{s.scenario || s}</span>
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#374151' }}>{s.percentage != null ? `${s.percentage}%` : '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Issues & Top Buy Reasons Section */}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Top Issues */}
          <div className="space-y-4 p-4 rounded-lg" style={{ backgroundColor: '#F5F5F5' }}>
            <h4 className="text-lg font-semibold text-gray-900">Top Issues</h4>
            <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
            <div className="space-y-4">
              {(Array.isArray(negativeSentiments) && negativeSentiments.length > 0 ? negativeSentiments.slice(0, 3) : []).map((n: any, idx: number) => (
                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="px-3 py-1 text-sm font-medium rounded-full mb-6" style={{ borderRadius: '500px', border: '1px solid #F02024', background: '#FFD8DE', color: '#F02024', width: 'fit-content', height: 'fit-content' }}>
                        {idx === 0 ? 'Critical' : idx === 1 ? 'Moderate' : 'Less Critical'}
                      </span>
                      <p style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>
                        {n.sentiment || n.theme || '—'}
                      </p>
                </div>
                    <div className="w-16 h-16 relative">
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#8B5CF6" strokeWidth="3" strokeDasharray={`${(n.percentage ?? 0)}, 100`} strokeDashoffset="0" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n.percentage != null ? `${n.percentage}%` : '—'}</span>
          </div>
        </div>
                </div>
              ))}

              {(Array.isArray(barriers) && barriers.length > 0 ? barriers.slice(0, 2) : []).map((b: any, idx: number) => (
                <div key={`bar-${idx}`} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="px-3 py-1 text-sm font-medium rounded-full mb-6" style={{ borderRadius: '500px', border: '1px solid #FFB44F', background: '#FFF0DD', color: '#FFB44F', width: 'fit-content', height: 'fit-content' }}>
                        Barrier
                      </span>
                      <p style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>
                        {typeof b === 'string' ? b : (b?.gap || b?.sentiment || '—')}
                      </p>
                </div>
                    <div className="w-16 h-16 relative">
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#8B5CF6" strokeWidth="3" strokeDasharray={`50, 100`} strokeDashoffset="0" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>—</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

          {/* Top Buy Reasons */}
          <div className="space-y-4 p-4 rounded-lg" style={{ backgroundColor: '#F5F5F5' }}>
            <h4 className="text-lg font-semibold text-gray-900">Top buy reasons</h4>
            <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />
            <div className="space-y-4">
              {(Array.isArray(firstDirect?.purchase_reasons) && firstDirect.purchase_reasons.length > 0
                ? firstDirect.purchase_reasons.slice(0, 3)
                : []
              ).map((r: any, idx: number) => (
                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="px-3 py-1 text-sm font-medium rounded-full mb-6" style={{ borderRadius: '500px', border: '1px solid #5A4ABE', background: '#F3F1FF', color: '#5A4ABE', width: 'fit-content', height: 'fit-content' }}>
                        {idx === 0 ? 'High Impact' : idx === 1 ? 'Medium Impact' : 'Low Impact'}
                      </span>
                      <p style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>
                        {r.reason || r}
                      </p>
                </div>
                    <div className="w-16 h-16 relative">
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#E5E7EB" strokeWidth="3" />
                        <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#8B5CF6" strokeWidth="3" strokeDasharray={`${(r.percentage ?? 0)}, 100`} strokeDashoffset="0" />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold" style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {r.percentage != null ? `${r.percentage}%` : '—'}
                      </span>
          </div>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>


      {/* Usage Insights Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
        background: '#F5F5F5',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
          }}>Usage Insights</h3>
      </div>
        
        <div className="space-y-6">
          {(Array.isArray(usageScenarios) ? usageScenarios.slice(0, 6) : []).map((u: any, index: number) => {
            const percent = u?.percentage ?? 0;
            const mentions = u?.mentions ?? null;
            return (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{u?.scenario || u}</h4>
                  <div className="flex items-center space-x-4">
                    {mentions != null && (
                      <span className="px-3 py-1 text-sm rounded-full" style={{ borderRadius: '500px', border: '1px solid #5A4ABE', background: '#F3F1FF', color: '#5A4ABE' }}>{mentions} Mentions</span>
            )}
          </div>
        </div>
                <div className="mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex-1 bg-gray-200 rounded-lg h-2" style={{ borderRadius: '8px', background: '#E0DEF6' }}>
                      <div className="h-2 rounded-lg transition-all duration-300" style={{ width: `${percent}%`, background: '#3D348B', borderRadius: '8px' }}></div>
                </div>
                    <span className="text-sm font-medium text-gray-900">{percent}%</span>
          </div>
        </div>
      </div>
            );
          })}
    </div>
    </div>

    {/* Competitor Keyword Analysis Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F4F3FF',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Competitor Keyword Analysis</h3>
      </div>
      <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #D1D5DB' }} />

              {/* Market Keyword Gaps */}
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ color: '#4A3B8E' }}>Market Keyword Gaps</h4>
            {competitorData.competitor_keyword_analysis?.market_keyword_gaps ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(competitorData.competitor_keyword_analysis.market_keyword_gaps || []).map((gap: any, index: number) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">{gap.gap}</h5>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        gap.opportunity === 'High' ? 'bg-green-100 text-green-800' :
                        gap.opportunity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {gap.opportunity} Opportunity
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Search Volume: {gap.search_volume}</p>
                    <p className="text-sm text-gray-600 mb-2">Competition: {gap.competition}</p>
                    <p className="text-xs text-gray-500">{gap.implementation}</p>
                  </div>
                ))}
              </div>
            ) : competitorData.keyword_analysis?.primary_keywords ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(competitorData.keyword_analysis.primary_keywords || []).slice(0, 6).map((keyword: any, index: number) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">{keyword.keyword}</h5>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        keyword.sales_potential === 'High' ? 'bg-green-100 text-green-800' :
                        keyword.sales_potential === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {keyword.sales_potential} Potential
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Search Volume: {keyword.search_volume}</p>
                    <p className="text-sm text-gray-600 mb-2">Trend: {keyword.trend}</p>
                    <p className="text-xs text-gray-500">Competition: {keyword.competitor_ranking}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <p className="text-gray-500">Keyword analysis data is being generated...</p>
              </div>
            )}
          </div>

                {/* Competitor Keyword Strategies */}
        <div>
          <h4 className="text-lg font-semibold mb-4" style={{ color: '#4A3B8E' }}>Competitor Keyword Strategies</h4>
          {competitorData.competitor_keyword_analysis?.competitor_keyword_strategies ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr style={{ backgroundColor: '#E2D5FF' }}>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Competitor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Strategy</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Keywords</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Strength</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Weakness</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(competitorData.competitor_keyword_analysis.competitor_keyword_strategies || []).map((strategy: any, index: number) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{strategy.competitor}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{strategy.strategy}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {Array.isArray(strategy.keywords) ? strategy.keywords.join(', ') : strategy.keywords}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{strategy.strength}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{strategy.weakness}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : competitorData.keyword_analysis?.long_tail_keywords ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr style={{ backgroundColor: '#E2D5FF' }}>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Keyword</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Search Volume</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Sales Potential</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Trend</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Competition</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(competitorData.keyword_analysis.long_tail_keywords || []).slice(0, 5).map((keyword: any, index: number) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{keyword.keyword}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{keyword.search_volume}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{keyword.sales_potential}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{keyword.trend}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{keyword.competitor_ranking}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
              <p className="text-gray-500">Keyword strategies data is being generated...</p>
            </div>
          )}
        </div>

        {/* Keyword Opportunities */}
        <div>
          <h4 className="text-lg font-semibold mb-4" style={{ color: '#4A3B8E' }}>Keyword Opportunities</h4>
          {competitorData.competitor_keyword_analysis?.keyword_opportunities ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(competitorData.competitor_keyword_analysis.keyword_opportunities || []).map((opportunity: any, index: number) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-gray-900">{opportunity.opportunity}</h5>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    opportunity.implementation_priority === 'High' ? 'bg-red-100 text-red-800' :
                    opportunity.implementation_priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {opportunity.implementation_priority} Priority
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">Potential Impact: {opportunity.potential_impact}</p>
                <p className="text-sm text-gray-600 mb-2">Competition Level: {opportunity.competition_level}</p>
                <div className="text-xs text-gray-500">
                  <strong>Target Keywords:</strong> {Array.isArray(opportunity.target_keywords) ? opportunity.target_keywords.join(', ') : opportunity.target_keywords}
                </div>
              </div>
            ))}
            </div>
          ) : competitorData.keyword_analysis?.secondary_keywords ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(competitorData.keyword_analysis.secondary_keywords || []).slice(0, 4).map((keyword: any, index: number) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-gray-900">{keyword.keyword}</h5>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      keyword.sales_potential === 'High' ? 'bg-green-100 text-green-800' :
                      keyword.sales_potential === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {keyword.sales_potential} Potential
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Search Volume: {keyword.search_volume}</p>
                  <p className="text-sm text-gray-600 mb-2">Trend: {keyword.trend}</p>
                  <p className="text-xs text-gray-500">Competition: {keyword.competitor_ranking}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
              <p className="text-gray-500">Keyword opportunities data is being generated...</p>
            </div>
          )}
        </div>

        {/* Keyword Ranking Analysis */}
        <div>
          <h4 className="text-lg font-semibold mb-4" style={{ color: '#4A3B8E' }}>Keyword Ranking Analysis</h4>
          {competitorData.competitor_keyword_analysis?.keyword_ranking_analysis ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr style={{ backgroundColor: '#E2D5FF' }}>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Keyword</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Current Ranking</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Target Ranking</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Effort Required</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Timeframe</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Strategy</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(competitorData.competitor_keyword_analysis.keyword_ranking_analysis || []).map((ranking: any, index: number) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ranking.keyword}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{ranking.current_ranking}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{ranking.target_ranking}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{ranking.effort_required}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{ranking.timeframe}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{ranking.strategy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : competitorData.keyword_analysis?.short_tail_keywords ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr style={{ backgroundColor: '#E2D5FF' }}>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Keyword</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Search Volume</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Sales Potential</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Ranking Difficulty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Competition</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(competitorData.keyword_analysis.short_tail_keywords || []).slice(0, 5).map((keyword: any, index: number) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{keyword.keyword}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{keyword.search_volume}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{keyword.sales_potential}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{keyword.ranking_difficulty}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{keyword.competitor_ranking}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
            <p className="text-gray-500">Keyword ranking analysis data is being generated...</p>
          </div>
        )}
        </div>
      </div>
    </div>

  </div>
  );
};

// Function to extract ASIN from Amazon URL
const extractASIN = (url: string): string | null => {
  if (!url) return null;
  
  // Handle different Amazon URL formats
  const patterns = [
    /\/dp\/([A-Z0-9]{10})/, // /dp/B0B1D2G1Y6
    /\/gp\/product\/([A-Z0-9]{10})/, // /gp/product/B0B1D2G1Y6
    /\/ASIN\/([A-Z0-9]{10})/, // /ASIN/B0B1D2G1Y6
    /\/ref=.*\/([A-Z0-9]{10})/, // /ref=.../B0B1D2G1Y6
    /\/([A-Z0-9]{10})(?:\/|$|\?)/, // /B0B1D2G1Y6/ or /B0B1D2G1Y6? or /B0B1D2G1Y6
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  // If no pattern matches, try to extract from the end of the URL
  const urlParts = url.split('/');
  const lastPart = urlParts[urlParts.length - 1];
  if (lastPart && /^[A-Z0-9]{10}$/.test(lastPart)) {
    return lastPart;
  }
  
  return null;
};

const getAllKeys = (obj: any, prefix = ''): string[] => {
  const keys: string[] = [];
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      keys.push(fullKey);
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        keys.push(...getAllKeys(obj[key], fullKey));
      }
    }
  }
  return keys;
};

const findPositiveSentiments = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.positive_sentiments && Array.isArray(obj.positive_sentiments)) {
    return obj.positive_sentiments;
  }
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
      const result = findPositiveSentiments(obj[key]);
      if (result) return result;
    }
  }
  return null;
};

const findNegativeSentiments = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.negative_sentiments && Array.isArray(obj.negative_sentiments)) {
    return obj.negative_sentiments;
  }
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
      const result = findNegativeSentiments(obj[key]);
      if (result) return result;
    }
  }
  return null;
};

const findExpectationGaps = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.expectation_gaps && Array.isArray(obj.expectation_gaps)) {
    return obj.expectation_gaps;
  }
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
      const result = findExpectationGaps(obj[key]);
      if (result) return result;
    }
  }
  return null;
};

const findBarriers = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.barriers && Array.isArray(obj.barriers)) {
    return obj.barriers;
  }
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
      const result = findBarriers(obj[key]);
      if (result) return result;
    }
  }
  return null;
};

const findRecurringQuestions = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.recurring_questions && Array.isArray(obj.recurring_questions)) {
    return obj.recurring_questions;
  }
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
      const result = findRecurringQuestions(obj[key]);
      if (result) return result;
    }
  }
  return null;
};

const findMessagingGaps = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.messaging_gaps && Array.isArray(obj.messaging_gaps)) {
    return obj.messaging_gaps;
  }
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
      const result = findMessagingGaps(obj[key]);
      if (result) return result;
    }
  }
  return null;
};

const findFaqs = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return null;
  
  if (obj.faqs && Array.isArray(obj.faqs)) {
    return obj.faqs;
  }
  
  if (obj.frequently_asked_questions && Array.isArray(obj.frequently_asked_questions)) {
    return obj.frequently_asked_questions;
  }
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && typeof obj[key] === 'object' && obj[key] !== null) {
      const result = findFaqs(obj[key]);
      if (result) return result;
    }
  }
  return null;
};

const KeywordsTab: React.FC<{ researchData?: any }> = ({ researchData }) => {
  // Get scraped data from the root level
  const scrapedData = researchData?.scraped_data || [];
  const amazonData = scrapedData.find((data: any) => data.type === 'amazon')?.raw_data;
  
  // Access data from the correct backend structure
  const keywordsData = researchData?.reports?.keywords?.content || researchData?.keywords;
  
  // Debug logging to see what data we're working with
  console.log('Keywords data debug:', {
    hasResearchData: !!researchData,
    hasKeywordsData: !!keywordsData,
    keywordsDataKeys: keywordsData ? Object.keys(keywordsData) : [],
    rawKeywordsData: keywordsData,
    // Backend structure analysis
    hasReports: !!researchData?.reports,
    reportsKeys: researchData?.reports ? Object.keys(researchData.reports) : [],
    keywordsReport: researchData?.reports?.keywords,
    keywordsContent: researchData?.reports?.keywords?.content
  });
  
  // Get real keywords from Amazon scraped data
  // const amazonKeywords = amazonData?.aiReviewsSummary?.keywords || [];
  
  // Access keyword data from the correct structure (keeping for potential future use)
  // const keywordAnalysis = keywordsData?.keyword_analysis || {};
  
  // Amazon keywords are not used in the Keywords tab - only backend AI-generated keywords are displayed
  
  // Get AI-generated keywords from backend ONLY
  const backendKeywords = keywordsData?.keyword_analysis || {};
  const backendPrimaryKeywords = backendKeywords.primary_keywords || [];
  const backendLongTailKeywords = backendKeywords.long_tail_keywords || [];
  const backendSecondaryKeywords = backendKeywords.secondary_keywords || [];
  const backendShortTailKeywords = backendKeywords.short_tail_keywords || [];
  const backendCompetitorKeywords = backendKeywords.competitor_keywords || [];
  
  // Use ONLY backend AI-generated keywords - no fallback or Amazon keywords
  let allKeywords = [
    ...backendPrimaryKeywords.map((kw: any) => ({ ...kw, category: 'Primary' })),
    ...backendLongTailKeywords.map((kw: any) => ({ ...kw, category: 'Long Tail' })),
    ...backendSecondaryKeywords.map((kw: any) => ({ ...kw, category: 'Secondary' })),
    ...backendShortTailKeywords.map((kw: any) => ({ ...kw, category: 'Short Tail' })),
    ...backendCompetitorKeywords.map((kw: any) => ({ ...kw, category: 'Competitor' }))
  ];
  
  // Only use fallback generation if no backend keywords are available at all
  if (allKeywords.length === 0) {
    
    // Extract comprehensive product intelligence
    const productTitle = amazonData?.title || researchData?.project?.product_name || 'Product';
    const productCategory = amazonData?.category || 'Health & Wellness';
    const productBrand = amazonData?.brand || productTitle.split(' ')[0];
    const productType = amazonData?.product_type || 'Supplement';
    const price = amazonData?.price?.value || amazonData?.price || 0;
    const rating = amazonData?.stars || 4.5;
    const reviewCount = amazonData?.reviewsCount || 1000;
    
    // Enhanced product analysis for better keyword generation
    const productWords = productTitle.toLowerCase().split(' ').filter((word: string) => word.length > 2);
    const isProtein = productWords.some((word: string) => ['protein', 'whey', 'casein', 'amino'].includes(word));
    const isVitamin = productWords.some((word: string) => ['vitamin', 'vitamins', 'mineral', 'supplement'].includes(word));
    const isFitness = productWords.some((word: string) => ['fitness', 'workout', 'gym', 'exercise', 'training'].includes(word));
    const isHealth = productWords.some((word: string) => ['health', 'wellness', 'natural', 'organic'].includes(word));
    
    // Determine product type based on analysis
    let detectedProductType = productType;
    if (isProtein) detectedProductType = 'Protein Supplement';
    else if (isVitamin) detectedProductType = 'Vitamin Supplement';
    else if (isFitness) detectedProductType = 'Fitness Supplement';
    else if (isHealth) detectedProductType = 'Health Supplement';
    
    // Advanced keyword generation based on product analysis
    const generateAdvancedKeywords = () => {
      const keywords = [];
      
             // Extract key product attributes for keyword generation (keeping for potential future use)
       // const titleWords = productTitle.toLowerCase().split(' ').filter((word: string) => word.length > 2);
       // const categoryWords = productCategory.toLowerCase().split(' ');
       // const brandWords = productBrand.toLowerCase().split(' ');
      
      // Primary Keywords - High commercial intent, brand-specific
      keywords.push({
        keyword: productTitle.substring(0, 60), // Full product title (truncated)
        category: 'Primary',
        search_volume: reviewCount > 5000 ? '15K-75K' : reviewCount > 1000 ? '8K-35K' : '3K-15K',
        trend: rating > 4.3 ? 'Growing' : rating > 4.0 ? 'Stable' : 'Declining',
        sales_potential: price > 50 ? 'High' : price > 25 ? 'Medium' : 'Low',
        ranking_difficulty: reviewCount > 10000 ? 'High' : reviewCount > 5000 ? 'Medium' : 'Low',
        relevance: 'High',
        brand_intent: 'Yes',
        search_intent: 'Commercial',
        expected_impact: 'High',
        placement_strategy: 'Product Landing Pages',
        competitor_ranking: reviewCount > 10000 ? 'Top 5' : reviewCount > 5000 ? 'Top 10' : 'Top 20'
      });
      
             // Brand-specific primary keywords
       keywords.push({
         keyword: `${productBrand} ${detectedProductType}`,
         category: 'Primary',
         search_volume: reviewCount > 5000 ? '5K-25K' : '2K-10K',
         trend: 'Growing',
         sales_potential: 'High',
         ranking_difficulty: 'Medium',
         relevance: 'High',
         brand_intent: 'Yes',
         search_intent: 'Commercial',
         expected_impact: 'High',
         placement_strategy: 'Brand Category Pages',
         competitor_ranking: 'Top 15'
       });
       
       // Product-specific primary keywords based on detected type
       if (isProtein) {
         keywords.push({
           keyword: `${productBrand} protein powder`,
           category: 'Primary',
           search_volume: '8K-40K',
           trend: 'Growing',
           sales_potential: 'High',
           ranking_difficulty: 'High',
           relevance: 'High',
           brand_intent: 'Yes',
           search_intent: 'Commercial',
           expected_impact: 'High',
           placement_strategy: 'Protein Category Pages',
           competitor_ranking: 'Top 10'
         });
       }
       
       if (isVitamin) {
         keywords.push({
           keyword: `${productBrand} vitamins`,
           category: 'Primary',
           search_volume: '6K-30K',
           trend: 'Growing',
           sales_potential: 'High',
           ranking_difficulty: 'Medium',
           relevance: 'High',
           brand_intent: 'Yes',
           search_intent: 'Commercial',
           expected_impact: 'High',
           placement_strategy: 'Vitamin Category Pages',
           competitor_ranking: 'Top 12'
         });
       }
      
             // Secondary Keywords - Medium intent, category-specific
       const secondaryTerms = [
         `${detectedProductType} benefits`,
         `best ${detectedProductType}`,
         `${detectedProductType} reviews`,
         `${detectedProductType} side effects`,
         `${detectedProductType} dosage`,
         `${detectedProductType} ingredients`
       ];
       
       // Add product-specific secondary keywords
       if (isProtein) {
         secondaryTerms.push(
           'protein powder benefits',
           'best protein powder',
           'whey protein reviews',
           'protein powder for muscle building'
         );
       }
       
       if (isVitamin) {
         secondaryTerms.push(
           'vitamin supplements benefits',
           'best vitamin supplements',
           'multivitamin reviews',
           'vitamin supplements for health'
         );
       }
      
      secondaryTerms.forEach((term, index) => {
        keywords.push({
          keyword: term,
          category: 'Secondary',
          search_volume: index < 2 ? '3K-15K' : '1K-8K',
          trend: index < 3 ? 'Growing' : 'Stable',
          sales_potential: index < 2 ? 'High' : 'Medium',
          ranking_difficulty: index < 2 ? 'High' : 'Medium',
          relevance: 'High',
          brand_intent: 'No',
          search_intent: index < 2 ? 'Commercial' : 'Informational',
          expected_impact: index < 2 ? 'High' : 'Medium',
          placement_strategy: index < 2 ? 'Comparison Pages' : 'Educational Content',
          competitor_ranking: index < 2 ? 'Top 10' : 'Top 20'
        });
      });
      
             // Long Tail Keywords - Specific intent, high conversion
       const longTailTerms = [
         `${detectedProductType} for beginners`,
         `${detectedProductType} vs ${detectedProductType} alternatives`,
         `${detectedProductType} weight loss`,
         `${detectedProductType} muscle building`,
         `${detectedProductType} energy boost`,
         `${detectedProductType} natural ingredients`,
         `${detectedProductType} without side effects`,
         `${detectedProductType} for women`,
         `${detectedProductType} for men`,
         `${detectedProductType} vegan`
       ];
       
       // Add product-specific long tail keywords
       if (isProtein) {
         longTailTerms.push(
           'protein powder for weight loss',
           'protein powder for muscle gain',
           'whey protein isolate vs concentrate',
           'protein powder for beginners',
           'best protein powder for women',
           'protein powder without artificial sweeteners'
         );
       }
       
       if (isVitamin) {
         longTailTerms.push(
           'vitamin supplements for energy',
           'multivitamin for women over 40',
           'vitamin supplements for immune system',
           'best vitamin supplements for men',
           'vitamin supplements for hair growth',
           'vitamin supplements for skin health'
         );
       }
      
      longTailTerms.forEach((term, index) => {
        keywords.push({
          keyword: term,
          category: 'Long Tail',
          search_volume: index < 5 ? '800-4K' : '300-2K',
          trend: 'Growing',
          sales_potential: index < 5 ? 'Medium' : 'Low',
          ranking_difficulty: 'Low',
          relevance: 'High',
          brand_intent: 'No',
          search_intent: 'Informational',
          expected_impact: 'Medium',
          placement_strategy: 'Niche Content Pages',
          competitor_ranking: 'Top 25'
        });
      });
      
             // Short Tail Keywords - High volume, broad terms
       const shortTailTerms = [
         detectedProductType,
         'supplements',
         'vitamins',
         'protein',
         'health',
         'fitness',
         'nutrition'
       ];
       
       // Add product-specific short tail keywords
       if (isProtein) {
         shortTailTerms.push('whey protein', 'protein powder', 'amino acids', 'muscle building');
       }
       
       if (isVitamin) {
         shortTailTerms.push('multivitamin', 'vitamin supplements', 'minerals', 'immune support');
       }
       
       if (isFitness) {
         shortTailTerms.push('workout supplements', 'pre workout', 'post workout', 'fitness supplements');
       }
       
       if (isHealth) {
         shortTailTerms.push('health supplements', 'wellness products', 'natural supplements', 'organic supplements');
       }
      
      shortTailTerms.forEach((term, index) => {
        keywords.push({
          keyword: term,
          category: 'Short Tail',
          search_volume: index < 3 ? '50K-300K' : '20K-100K',
          trend: 'Stable',
          sales_potential: 'High',
          ranking_difficulty: 'Very High',
          relevance: index < 3 ? 'High' : 'Medium',
          brand_intent: 'No',
          search_intent: 'Commercial',
          expected_impact: 'High',
          placement_strategy: 'General Landing Pages',
          competitor_ranking: 'Top 30'
        });
      });
      
             // Competitor Keywords - Market positioning
       const competitorTerms = [
         `${productBrand} vs competitors`,
         `${detectedProductType} comparison`,
         `best ${detectedProductType} brands`,
         `${detectedProductType} price comparison`
       ];
       
       // Add product-specific competitor keywords
       if (isProtein) {
         competitorTerms.push(
           'protein powder comparison',
           'best protein powder brands',
           'whey protein vs casein',
           'protein powder price comparison'
         );
       }
       
       if (isVitamin) {
         competitorTerms.push(
           'vitamin supplements comparison',
           'best vitamin brands',
           'multivitamin comparison',
           'vitamin supplements price comparison'
         );
       }
      
      competitorTerms.forEach((term) => {
        keywords.push({
          keyword: term,
          category: 'Competitor',
          search_volume: '1K-5K',
          trend: 'Growing',
          sales_potential: 'Medium',
          ranking_difficulty: 'Medium',
          relevance: 'High',
          brand_intent: 'No',
          search_intent: 'Commercial',
          expected_impact: 'Medium',
          placement_strategy: 'Comparison Pages',
          competitor_ranking: 'Top 15'
        });
      });
      
      return keywords;
    };
    
    allKeywords = generateAdvancedKeywords();
    
    console.log('Generated keywords debug:', {
      productTitle,
      productCategory,
      productBrand,
      productType,
      price,
      rating,
      reviewCount,
      keywordCount: allKeywords.length,
      allKeywords: allKeywords
    });
  } else {
    console.log('No keywords generated - using fallback');
  }
  
  console.log('Backend keywords debug:', {
    backendKeywordsCount: backendPrimaryKeywords.length + backendLongTailKeywords.length + backendSecondaryKeywords.length + backendShortTailKeywords.length + backendCompetitorKeywords.length,
    totalKeywords: allKeywords.length,
    backendKeywords: backendKeywords,
    usingOnlyBackendKeywords: true
  });

  // Dynamic table headers based on backend data structure
  const tableHeaders = [
    { key: 'keyword', label: 'Keyword' },
    { key: 'category', label: 'Category' },
    { key: 'search_volume', label: 'Search Volume' },
    { key: 'trend', label: 'Trend' },
    { key: 'sales_potential', label: 'Sales Potential' },
    { key: 'ranking_difficulty', label: 'Difficulty' },
    { key: 'relevance', label: 'Relevance' },
    { key: 'brand_intent', label: 'Brand Intent' },
    { key: 'search_intent', label: 'Search Intent' },
    { key: 'expected_impact', label: 'Expected Impact' },
    { key: 'placement_strategy', label: 'Placement Strategy' },
    { key: 'competitor_ranking', label: 'Competitor Rank' }
  ];

  // Wider columns for pill content to avoid wrapping
  const colMinWidths: Record<string, number> = {
    keyword: 200,
    category: 140,
    search_volume: 140,
    trend: 220,
    sales_potential: 220,
    ranking_difficulty: 140,
    relevance: 160,
    brand_intent: 200,
    search_intent: 160,
    expected_impact: 220,
    placement_strategy: 200,
    competitor_ranking: 180,
  };

  // Debug logging for table rendering
  console.log('Table rendering debug:', {
    allKeywordsLength: allKeywords.length,
    allKeywords: allKeywords,
    tableHeaders: tableHeaders,
    hasKeywords: allKeywords.length > 0
  });

  return (
  <div className="space-y-8">
    {/* Keyword Discovery & Analysis Table */}
    <div style={{
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Keyword Discovery & Analysis</h3>
      </div>
      
             {/* Table */}
          <div className="overflow-x-auto" style={{ 
            backgroundColor: '#FFFFFF', 
            borderRadius: '12px',
            border: '1px solid #C8B4F2'
          }}>
         <table className="min-w-full" style={{ minWidth: '1400px' }}>
              <thead>
                <tr style={{ 
                  backgroundColor: '#E2D5FF', 
                  borderRadius: '12px 12px 0 0',
                  color: '#4A3B8E'
                }}>
                {tableHeaders.map((header, index) => (
                  <th 
                    key={header.key}
                    className="text-left py-4 px-6 font-medium align-top" 
                    style={{ 
                      borderRadius: index === 0 ? '12px 0 0 0' : 
                                 index === tableHeaders.length - 1 ? '0 12px 0 0' : '0',
                      verticalAlign: 'top',
                      minWidth: `${colMinWidths[header.key] || 120}px`,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {header.label}
                  </th>
                ))}
                </tr>
              </thead>
              <tbody>
             {allKeywords.length === 0 ? (
               <tr>
                 <td colSpan={tableHeaders.length} className="py-8 text-center text-gray-600">
                   No keyword data available. Research reports may still be generating.
                 </td>
               </tr>
             ) : (
               allKeywords.map((keyword: any, index: number) => (
                  <tr key={index} style={{ 
                    backgroundColor: '#FAF8FF',
                    borderBottom: index < allKeywords.length - 1 ? '1px solid #C8B4F2' : 'none'
                  }}>
                 {tableHeaders.map((header) => {
                   const value = keyword[header.key];
                   
                   // Render different cell types based on the field
                   if (header.key === 'sales_potential' || header.key === 'expected_impact') {
                     return (
                       <td key={header.key} className="py-4 px-6 text-sm" style={{ minWidth: `${colMinWidths[header.key] || 180}px`, whiteSpace: 'nowrap' }}>
                      <span className="px-3 py-1 text-xs font-medium" style={{ 
                        borderRadius: '32px',
                           border: value === 'High' ? '1px solid #1BCE30' : 
                                  value === 'Medium' ? '1px solid #E0B600' : '1px solid #E22323',
                           backgroundColor: value === 'High' ? '#E0FFD7' : 
                                          value === 'Medium' ? '#FFF7D5' : '#FFCFCF',
                           color: value === 'High' ? '#1BCE30' : 
                                  value === 'Medium' ? '#E0B600' : '#E22323'
                         }}>
                           {value || 'N/A'}
                      </span>
                    </td>
                     );
                   }
                   
                   if (header.key === 'relevance' || header.key === 'brand_intent' || header.key === 'competitor_ranking') {
                     return (
                       <td key={header.key} className="py-4 px-6 text-sm" style={{ minWidth: `${colMinWidths[header.key] || 160}px`, whiteSpace: 'nowrap' }}>
                      <span className="px-3 py-1 text-xs font-medium" style={{ 
                        borderRadius: '32px',
                           border: value === 'High' || value === 'Yes' ? '1px solid #1BCE30' : '1px solid #E22323',
                           backgroundColor: value === 'High' || value === 'Yes' ? '#E0FFD7' : '#FFCFCF',
                           color: value === 'High' || value === 'Yes' ? '#1BCE30' : '#E22323'
                         }}>
                           {value || 'N/A'}
                      </span>
                    </td>
                     );
                   }
                   
                   return (
                     <td key={header.key} className="py-4 px-6 text-sm" style={{ color: '#111827' }}>
                       {value || 'N/A'}
                     </td>
                   );
                 })}
                  </tr>
             ))
             )}
              </tbody>
            </table>
          </div>
      </div>
    </div>
  );
};

const ReviewsTab: React.FC<{ researchData?: any }> = ({ researchData }) => {
  const getFirstWord = (val: any, fallback: string) => {
    const s = (val ?? '').toString().trim();
    if (!s) return fallback;
    // split on space or hyphen
    const part = s.split(/[-\s]+/)[0];
    return part || fallback;
  };
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Access data from the correct backend structure
  const reviewsData = researchData?.reports?.reviews?.content;
  
  // Get actual Amazon scraped data for real review counts and ratings
  // The scraped_data is at the root level of the dashboard response
  const scrapedData = researchData?.scraped_data || [];
  const amazonData = scrapedData.find((data: any) => data.type === 'amazon')?.raw_data;
  
  // Use real Amazon data for total reviews and average rating
  const actualTotalReviews = amazonData?.reviewsCount;
  const actualAverageRating = amazonData?.stars;
  
  // Format the numbers properly
  const formattedTotalReviews = actualTotalReviews ? actualTotalReviews.toLocaleString() : null;
  const formattedAverageRating = actualAverageRating ? actualAverageRating.toFixed(1) : null;
  
  console.log('Reviews data debug:', {
    hasScrapedData: !!scrapedData,
    scrapedDataLength: scrapedData.length,
    amazonData: amazonData,
    actualTotalReviews,
    actualAverageRating,
    formattedTotalReviews,
    formattedAverageRating,
    reviewsDataTotalReviews: reviewsData?.customer_sentiments?.total_reviews,
    reviewsDataAverageRating: reviewsData?.customer_sentiments?.average_rating,
    // Show the full scraped data structure
    scrapedDataTypes: scrapedData.map((data: any) => data.type),
    amazonDataKeys: amazonData ? Object.keys(amazonData) : []
  });
  
  // Debug logging to see what data we're working with
  console.log('Reviews data debug:', {
    hasResearchData: !!researchData,
    hasReviewsData: !!reviewsData,
    reviewsDataKeys: reviewsData ? Object.keys(reviewsData) : [],
    customerSentiments: reviewsData?.customer_sentiments,
    positiveSentiments: reviewsData?.customer_sentiments?.positive_sentiments,
    negativeSentiments: reviewsData?.customer_sentiments?.negative_sentiments,
    rawReviewsData: reviewsData,
    // Backend structure analysis
    hasReports: !!researchData?.reports,
    reportsKeys: researchData?.reports ? Object.keys(researchData.reports) : [],
    reviewsReport: researchData?.reports?.reviews,
    reviewsContent: researchData?.reports?.reviews?.content
  });

  return (
  <div className="space-y-8">
    {/* Customer Sentiments Header Section */}
    <div style={{
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Customer Sentiments</h3>
      </div>
      
      {/* Total Reviews and Average Rating Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="flex items-center p-4 rounded-lg" style={{ backgroundColor: '#F4F3FF', border: '1px solid #E2D5FF' }}>
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4">
            <BarChart3 className="h-5 w-5" style={{ color: '#4A3B8E' }} />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium" style={{ color: '#4A3B8E' }}>Total Reviews</span>
            <span className="text-lg font-semibold" style={{ color: '#4A3B8E' }}>{formattedTotalReviews || reviewsData?.customer_sentiments?.total_reviews || '356'}</span>
          </div>
        </div>

        <div className="flex items-center p-4 rounded-lg" style={{ backgroundColor: '#F4F3FF', border: '1px solid #E2D5FF' }}>
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4">
            <BarChart3 className="h-5 w-5" style={{ color: '#4A3B8E' }} />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium" style={{ color: '#4A3B8E' }}>Average Rating</span>
            <span className="text-lg font-semibold" style={{ color: '#4A3B8E' }}>{formattedAverageRating || reviewsData?.customer_sentiments?.average_rating || '4.6'}/5</span>
          </div>
        </div>
      </div>
    </div>

    {/* Positive Reviews Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F5F5F5',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Positive Reviews</h3>
      </div>
      
      <div className="space-y-6">
        {reviewsData?.customer_sentiments?.positive_sentiments && reviewsData.customer_sentiments.positive_sentiments.length > 0 ? (
          reviewsData.customer_sentiments.positive_sentiments.map((review: any, index: number) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{review.sentiment || 'Positive Review'}</h4>
                <div className="flex items-center space-x-4">
                  <span className="px-3 py-1 text-sm rounded-full" style={{ borderRadius: '500px', border: '1px solid #5A4ABE', background: '#F3F1FF', color: '#5A4ABE' }}>{review.theme || 'Theme'}</span>
                </div>
              </div>
              <p style={{ color: '#85878B', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', width: '85%' }} className="mb-4">
                {review.example_reviews && review.example_reviews.length > 0 ? 
                  `"${review.example_reviews[0]}"` : 
                  'Customer feedback about positive experience.'
                }
              </p>
              <div className="mb-4">
                <div className="flex items-center space-x-4">
                  <div className="flex-1 bg-gray-200 rounded-lg h-2" style={{ borderRadius: '8px', background: '#E0DEF6' }}>
                    <div 
                      className="h-2 rounded-lg transition-all duration-300"
                      style={{ width: `${review.percentage || 0}%`, background: '#3D348B', borderRadius: '8px' }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{review.percentage || 0}%</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading positive reviews from backend...</p>
          </div>
        )}
      </div>
    </div>

          {/* Negative Reviews Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F5F5F5',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Negative Reviews</h3>
      </div>
      
      <div className="space-y-6">
        {reviewsData?.customer_sentiments?.negative_sentiments && reviewsData.customer_sentiments.negative_sentiments.length > 0 ? (
          reviewsData.customer_sentiments.negative_sentiments.map((review: any, index: number) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 style={{ color: '#DC2626', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{review.sentiment || 'Negative Review'}</h4>
                <div className="flex items-center space-x-4">
                  <span className="px-3 py-1 text-sm rounded-full" style={{ borderRadius: '500px', border: '1px solid #DC2626', background: '#FEF2F2', color: '#DC2626' }}>{review.theme || 'Theme'}</span>
                </div>
              </div>
              <p style={{ color: '#85878B', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', width: '85%' }} className="mb-4">
                {review.example_reviews && review.example_reviews.length > 0 ? 
                  `"${review.example_reviews[0]}"` : 
                  'Customer feedback about negative experience.'
                }
              </p>
              <div className="mb-4">
                <div className="flex items-center space-x-4">
                  <div className="flex-1 bg-gray-200 rounded-lg h-2" style={{ borderRadius: '8px', background: '#FEE2E2' }}>
                    <div 
                      className="h-2 rounded-lg transition-all duration-300"
                      style={{ width: `${review.percentage || 0}%`, background: '#DC2626', borderRadius: '8px' }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{review.percentage || 0}%</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading negative reviews from backend...</p>
          </div>
        )}
      </div>
    </div>

    {/* Expectation/Unmet Needs and Barriers to Purchase Section */}
    <div className="flex gap-8">
      {/* Expectation/Unmet Needs */}
      <div style={{
        borderRadius: '20px',
        border: '1px solid #E2D5FF',
        background: '#F5F5F5',
        padding: '24px',
        gap: '32px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }}>
        <div className="flex items-center space-x-2">
          <CustomUserIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-xl font-semibold text-gray-900" style={{
            color: '#4A3B8E',
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 'normal',
            textTransform: 'capitalize'
          }}>Expectation/Unmet Needs</h3>
        </div>
        
                 <div className="space-y-6">
           {reviewsData?.customer_sentiments?.expectation_gaps && reviewsData.customer_sentiments.expectation_gaps.length > 0 ? (
             reviewsData.customer_sentiments.expectation_gaps.map((item: any, index: number) => (
               <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                 <div className="flex items-center justify-between mb-4">
                   <h4 style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{item.gap || 'Expectation Gap'}</h4>
                   <div className="flex items-center space-x-4">
                    <span className="px-3 py-1 text-sm rounded-full" style={{ borderRadius: '500px', border: '1px solid #5A4ABE', background: '#F3F1FF', color: '#5A4ABE' }}>{getFirstWord(item.frequency, 'Occasional')}</span>
                   </div>
                 </div>
                 <p style={{ color: '#85878B', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', width: '85%' }} className="mb-4">{item.impact || 'Impact description of the expectation gap.'}</p>
               </div>
             ))
           ) : (
             <div className="text-center py-8">
               <p className="text-gray-500">Loading expectation gaps from backend...</p>
             </div>
           )}
         </div>
      </div>

      {/* Barriers to Purchase */}
      <div style={{
        borderRadius: '20px',
        border: '1px solid #E2D5FF',
        background: '#F5F5F5',
        padding: '24px',
        gap: '32px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }}>
        <div className="flex items-center space-x-2">
          <CustomUserIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-xl font-semibold text-gray-900" style={{
            color: '#4A3B8E',
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 'normal',
            textTransform: 'capitalize'
          }}>Barriers to Purchase</h3>
        </div>
        
                 <div className="space-y-6">
           {reviewsData?.customer_sentiments?.barriers && reviewsData.customer_sentiments.barriers.length > 0 ? (
             reviewsData.customer_sentiments.barriers.map((item: any, index: number) => (
               <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                 <div className="flex items-center justify-between mb-4">
                   <h4 style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{item.barrier || 'Purchase Barrier'}</h4>
                   <div className="flex items-center space-x-4">
                    <span className="px-3 py-1 text-sm rounded-full" style={{ borderRadius: '500px', border: '1px solid #5A4ABE', background: '#F3F1FF', color: '#5A4ABE' }}>{getFirstWord(item.type, 'Occasional')}</span>
                   </div>
                 </div>
                 <p style={{ color: '#85878B', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', width: '85%' }} className="mb-4">{item.impact || 'Impact description of the purchase barrier.'}</p>
                 <p style={{ color: '#6B7280', fontSize: '12px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal' }} className="mt-2">
                   <strong>Mitigation:</strong> {item.mitigation_strategy || 'Strategy to address this barrier.'}
                 </p>
               </div>
             ))
           ) : (
             <div className="text-center py-8">
               <p className="text-gray-500">Loading purchase barriers from backend...</p>
             </div>
           )}
         </div>
      </div>
    </div>

    {/* Review Patterns Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F5F5F5',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Review Patterns & Insights</h3>
      </div>
      
      <div className="space-y-6">
        {reviewsData?.review_patterns && reviewsData.review_patterns.length > 0 ? (
          reviewsData.review_patterns.map((pattern: any, index: number) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{pattern.pattern || 'Review Pattern'}</h4>
                <div className="flex items-center space-x-4">
                  <span className="px-3 py-1 text-sm rounded-full" style={{ borderRadius: '500px', border: '1px solid #5A4ABE', background: '#F3F1FF', color: '#5A4ABE' }}>{pattern.frequency || 'Frequent'}</span>
                </div>
              </div>
              <p style={{ color: '#85878B', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', width: '85%' }} className="mb-4">{pattern.description || 'Description of the review pattern.'}</p>
              <p style={{ color: '#6B7280', fontSize: '12px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal' }} className="mt-2">
                <strong>Insight:</strong> {pattern.insight || 'Key insight from this pattern.'}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading review patterns from backend...</p>
          </div>
        )}
      </div>
    </div>



    {/* Sentiment Trends Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F5F5F5',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Sentiment Trends</h3>
      </div>
      
      <div className="space-y-6">
        {reviewsData?.sentiment_trends && reviewsData.sentiment_trends.length > 0 ? (
          reviewsData.sentiment_trends.map((trend: any, index: number) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{trend.trend || 'Sentiment Trend'}</h4>
              </div>
              <p style={{ color: '#85878B', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', width: '85%' }} className="mb-4">{trend.description || 'Description of the sentiment trend.'}</p>
              <p style={{ color: '#6B7280', fontSize: '12px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal' }} className="mt-2">
                <strong>Impact:</strong> {trend.impact || 'Impact of this trend.'}
              </p>
              <p style={{ color: '#6B7280', fontSize: '12px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal' }} className="mt-1">
                <strong>Recommendation:</strong> {trend.recommendation || 'Recommendation based on this trend.'}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading sentiment trends from backend...</p>
          </div>
        )}
      </div>
    </div>

    {/* Actionable Insights Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F5F5F5',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Actionable Insights</h3>
      </div>
      
      <div className="space-y-6">
        {reviewsData?.actionable_insights && reviewsData.actionable_insights.length > 0 ? (
          reviewsData.actionable_insights.map((insight: any, index: number) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 style={{ color: '#3D348B', fontSize: '16px', fontStyle: 'normal', fontWeight: '600', lineHeight: 'normal' }}>{insight.insight || 'Actionable Insight'}</h4>
                <div className="flex items-center space-x-4">
                  <span className="px-3 py-1 text-sm rounded-full" style={{ borderRadius: '500px', border: '1px solid #5A4ABE', background: '#F3F1FF', color: '#5A4ABE' }}>{insight.category || 'Category'}</span>
                  <span className={`px-3 py-1 text-sm rounded-full ${
                    insight.priority === 'High' ? 'border-red-500 bg-red-50 text-red-700' :
                    insight.priority === 'Medium' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' :
                    'border-green-500 bg-green-50 text-green-700'
                  }`}>
                    {insight.priority || 'Priority'}
                  </span>
                </div>
              </div>
              <p style={{ color: '#85878B', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', width: '85%' }} className="mb-4">{insight.action || 'Action to take.'}</p>
              <p style={{ color: '#6B7280', fontSize: '12px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal' }} className="mt-2">
                <strong>Expected Impact:</strong> {insight.expected_impact || 'Expected impact of this action.'}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading actionable insights from backend...</p>
          </div>
        )}
      </div>
    </div>

    {/* FAQs Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F5F5F5',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>FAQs</h3>
      </div>
      
      <div className="space-y-4">
                 {/* Use real FAQ data from Amazon scraped data */}
         {(() => {
           // Get real FAQ data from Amazon scraped data - convert keywords to proper Q&A format
           const realFaqs = amazonData?.aiReviewsSummary?.keywords?.map((keyword: any) => {
             // Convert keyword name to a proper question format
             const question = `What do customers say about ${keyword.name.toLowerCase()}?`;
             
             return {
               question: question,
               answer: keyword.text, // This is the actual answer/analysis
               frequency: `${keyword.customersMentionedCount?.total || 0} mentions`,
               answer_quality: keyword.sentiment === 'positive' ? 'Positive sentiment' : 'Mixed sentiment'
             };
           }) || [];
           
           // Also include recurring questions from AI analysis if available
           const aiFaqs = reviewsData?.customer_sentiments?.recurring_questions?.map((faq: any) => ({
             question: faq.question,
             answer: faq.answer_quality || 'Based on customer analysis',
             frequency: faq.frequency || 'Common',
             answer_quality: 'AI Analysis'
           })) || [];
           
           // Combine real and AI FAQs, prioritizing real data
           const allFaqs = [...realFaqs, ...aiFaqs];
          
          console.log('FAQs debug:', {
            realFaqsCount: realFaqs.length,
            aiFaqsCount: aiFaqs.length,
            totalFaqs: allFaqs.length,
            realFaqs: realFaqs,
            aiFaqs: aiFaqs
          });
          
          if (allFaqs.length > 0) {
            return allFaqs.map((faq: any, index: number) => (
            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden" style={{ backgroundColor: '#FFFFFF' }}>
              <button
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors duration-200"
                onClick={() => toggleFaq(index)}
              >
                <span className="text-sm font-medium" style={{ color: '#4A3B8E' }}>{faq.question || 'Customer Question'}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">{faq.frequency || 'Frequent'}</span>
                  <svg 
                    className={`transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`} 
                    width="14" 
                    height="11" 
                    viewBox="0 0 14 11" 
                    fill="none"
                  >
                    <path 
                      fillRule="evenodd" 
                      clipRule="evenodd" 
                      d="M8.38144 9.59654L13.584 3.57125C13.7929 3.2932 13.9304 2.94811 13.9797 2.57784C14.029 2.20756 13.988 1.82801 13.8618 1.48522C13.7355 1.14243 13.5294 0.851131 13.2684 0.646653C13.0074 0.442175 12.7028 0.333307 12.3914 0.333252H1.60406C1.29367 0.334353 0.990172 0.443579 0.730159 0.647759C0.470146 0.85194 0.264724 1.14236 0.138661 1.48399C0.0125983 1.82562 -0.0287212 2.20389 0.0196848 2.57317C0.0680907 2.94245 0.204155 3.28698 0.411471 3.56522L5.61406 9.59654C5.7894 9.8286 6.00305 10.014 6.24132 10.1409C6.4796 10.2677 6.73724 10.3333 6.99775 10.3333C7.25826 10.3333 7.5159 10.2677 7.75417 10.1409C7.99244 10.014 8.20609 9.8286 8.38144 9.59654Z" 
                      fill="#797979"
                    />
                  </svg>
                </div>
              </button>
              {openFaqIndex === index && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <p className="text-sm mt-3" style={{ color: '#6B7280', lineHeight: '1.6' }}>
                      <strong>Analysis:</strong> {faq.answer}
                  </p>
                </div>
              )}
            </div>
            ));
          } else {
            return (
          <div className="text-center py-8">
                <p className="text-gray-500">No FAQ data available from Amazon reviews.</p>
          </div>
            );
          }
        })()}
      </div>
    </div>
  </div>
  );
};

const DiscoveryTab: React.FC<{ researchData?: any }> = ({ researchData }) => {
  // Access data from the correct backend structure
  const discoveryData = researchData?.reports?.discovery?.content;
  
  // Enhanced debug logging to see what data we're working with
  console.log('Discovery data debug:', {
    hasResearchData: !!researchData,
    hasDiscoveryData: !!discoveryData,
    discoveryDataKeys: discoveryData ? Object.keys(discoveryData) : [],
    productFeatures: discoveryData?.product_features,
    usps: discoveryData?.usps,
    variants: discoveryData?.variants,
    certifications: discoveryData?.certifications,
    brandBackground: discoveryData?.brand_background,
    founderStory: discoveryData?.founder_story,
    ingredients: discoveryData?.ingredients,
    rawDiscoveryData: discoveryData,
    // Backend structure analysis
    hasReports: !!researchData?.reports,
    reportsKeys: researchData?.reports ? Object.keys(researchData.reports) : [],
    discoveryReport: researchData?.reports?.discovery,
    discoveryContent: researchData?.reports?.discovery?.content,
    // Detailed field analysis
    variantsType: typeof discoveryData?.variants,
    variantsLength: discoveryData?.variants?.length,
    variantsContent: discoveryData?.variants,
    certificationsType: typeof discoveryData?.certifications,
    certificationsLength: discoveryData?.certifications?.length,
    certificationsContent: discoveryData?.certifications,
    founderStoryType: typeof discoveryData?.founder_story,
    founderStoryLength: discoveryData?.founder_story?.length,
    founderStoryContent: discoveryData?.founder_story,
    // Check if data is string that needs parsing
    isVariantsString: typeof discoveryData?.variants === 'string',
    isCertificationsString: typeof discoveryData?.certifications === 'string',
    isFounderStoryString: typeof discoveryData?.founder_story === 'string',
  });

  // Try to parse data if it's a string
  let parsedVariants = discoveryData?.variants;
  let parsedCertifications = discoveryData?.certifications;
  let parsedFounderStory = discoveryData?.founder_story;

  if (typeof discoveryData?.variants === 'string') {
    try {
      parsedVariants = JSON.parse(discoveryData.variants);
    } catch (e) {
    }
  }

  if (typeof discoveryData?.certifications === 'string') {
    try {
      parsedCertifications = JSON.parse(discoveryData.certifications);
    } catch (e) {
    }
  }

  if (typeof discoveryData?.founder_story === 'string') {
    try {
      parsedFounderStory = JSON.parse(discoveryData.founder_story);
    } catch (e) {
    }
  }

  // Use parsed data or original data
  const finalVariants = parsedVariants || discoveryData?.variants;
  const finalCertifications = parsedCertifications || discoveryData?.certifications;
  const finalFounderStory = parsedFounderStory || discoveryData?.founder_story;

  console.log('Parsed data debug:', {
    finalVariants,
    finalCertifications,
    finalFounderStory,
    variantsValid: Array.isArray(finalVariants) && finalVariants.length > 0,
    certificationsValid: Array.isArray(finalCertifications) && finalCertifications.length > 0,
    founderStoryValid: typeof finalFounderStory === 'string' && finalFounderStory.length > 0,
  });

  return (
   <div className="space-y-8">
     {/* User Section */}
     <div>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Mandatory Sections</h3>
      </div>
     </div>

     {/* Product Features & Technical Specifications Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F4F3FF',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Product Features & Technical Specifications</h3>
      </div>
      {/* List View of Features */}
      <div className="space-y-4">
        {discoveryData?.product_features && discoveryData.product_features.length > 0 ? (
          discoveryData.product_features.map((feature: any, index: number) => (
            <div key={index} className="p-4" style={{ 
              backgroundColor: '#FFFFFF', 
              borderRadius: '12px',
              border: '1px solid #E2D5FF'
            }}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-semibold" style={{ color: '#3D348B' }}>{feature.feature || 'Product Feature'}</h4>
                <div className="flex items-center space-x-3">
                  <span className="px-3 py-1 text-xs font-medium" style={{ 
                    borderRadius: '500px',
                    border: '1px solid #FFB44F',
                    background: '#FFF0DD',
                    color: '#FFB44F'
                  }}>
                    {feature.importance || 'High'}
                  </span>
                  <span className="px-3 py-1 text-xs font-medium" style={{ 
                    borderRadius: '500px',
                    border: '1px solid #5A4ABE',
                    background: '#F3F1FF',
                    color: '#5A4ABE'
                  }}>
                    {feature.category || 'Feature'}
                  </span>
                </div>
              </div>
              <p className="text-sm" style={{ color: '#85878B' }}>
                {feature.description || 'Description of the product feature.'}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading product features from backend...</p>
          </div>
        )}
      </div>
    </div>

    {/* USPs Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F4F3FF',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>USPs</h3>
      </div> 
      
      <div className="space-y-4" style={{ backgroundColor: '#FFFFFF', padding: '16px', borderRadius: '12px' }}>
        {discoveryData?.usps && discoveryData.usps.length > 0 ? (
          discoveryData.usps.map((usp: any, index: number) => (
            <div key={index}>
              <div className="flex items-start space-x-4">
                {/* Purple vertical line */}
                <div className="w-1 h-12" style={{ backgroundColor: '#8B5CF6' }}></div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-2" style={{ color: '#4A3B8E' }}>{usp.usp || 'Unique Selling Point'}</h4>
                  <p className="text-sm" style={{ color: '#4A3B8E' }}>{usp.differentiation || 'Differentiation from competitors.'}</p>
                  <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                    <strong>Customer Value:</strong> {usp.customer_value || 'Value to customers.'}
                  </p>
                </div>
              </div>
              {index < discoveryData.usps.length - 1 && (
                <hr style={{ 
                  margin: '16px 0', 
                  border: 'none', 
                  borderTop: '1px solid #E5E7EB' 
                }} />
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading USPs from backend...</p>
          </div>
        )}
      </div>
    </div>

    {/* Functional Benefits Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F4F3FF',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Functional Benefits</h3>
      </div> 
      
      <div className="space-y-4" style={{ backgroundColor: '#FFFFFF', padding: '16px', borderRadius: '12px' }}>
        {discoveryData?.functional_benefits && discoveryData.functional_benefits.length > 0 ? (
          discoveryData.functional_benefits.map((benefit: any, index: number) => (
            <div key={index}>
              <div className="flex items-start space-x-4">
                {/* Purple vertical line */}
                <div className="w-1 h-12" style={{ backgroundColor: '#8B5CF6' }}></div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-2" style={{ color: '#4A3B8E' }}>{benefit}</h4>
                </div>
              </div>
              {index < discoveryData.functional_benefits.length - 1 && (
                <hr style={{ 
                  margin: '16px 0', 
                  border: 'none', 
                  borderTop: '1px solid #E5E7EB' 
                }} />
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading functional benefits from backend...</p>
          </div>
        )}
      </div>
    </div>

    {/* Emotional Benefits Section */}
    <div style={{
      borderRadius: '20px',
      border: '1px solid #E2D5FF',
      background: '#F4F3FF',
      padding: '24px',
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Emotional Benefits</h3>
      </div> 
      
      <div className="space-y-4" style={{ backgroundColor: '#FFFFFF', padding: '16px', borderRadius: '12px' }}>
        {discoveryData?.emotional_benefits && discoveryData.emotional_benefits.length > 0 ? (
          discoveryData.emotional_benefits.map((benefit: any, index: number) => (
            <div key={index}>
              <div className="flex items-start space-x-4">
                {/* Purple vertical line */}
                <div className="w-1 h-12" style={{ backgroundColor: '#8B5CF6' }}></div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-2" style={{ color: '#4A3B8E' }}>{benefit}</h4>
                </div>
              </div>
              {index < discoveryData.emotional_benefits.length - 1 && (
                <hr style={{ 
                  margin: '16px 0', 
                  border: 'none', 
                  borderTop: '1px solid #E5E7EB' 
                }} />
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading emotional benefits from backend...</p>
          </div>
        )}
      </div>
    </div>

    {/* Conditional Sections */}
    <div style={{
      gap: '32px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="flex items-center space-x-2">
        <CustomUserIcon className="h-5 w-5 text-gray-600" />
        <h3 className="text-xl font-semibold text-gray-900" style={{
          color: '#4A3B8E',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
          textTransform: 'capitalize'
        }}>Conditional Sections</h3>
      </div>
      
      <div style={{ backgroundColor: '#F4F3FF', padding: '16px', borderRadius: '12px', border: '1px solid #E2D5FF' }}>
        <div className="space-y-8">
          {/* About Brand */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>About Brand</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {discoveryData?.brand_background || 'Brand background information is being generated. This will include the company history, mission, values, and key achievements that define the brand identity.'}
              </p>
            </div>
          </div>

          {/* What's Included */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>What's Included</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {discoveryData?.package_contents && discoveryData.package_contents.length > 0 ? 
                  discoveryData.package_contents.map((item: any) => `${item.item}: ${item.description}`).join(', ') :
                  'Package contents are being analyzed. This will detail all items included in the product package, such as the main product, accessories, instructions, and any additional components.'
                }
              </p>
            </div>
          </div>

          {/* Variants */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>Variants</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {(() => {
                  console.log('Variants debug:', {
                    hasVariants: !!finalVariants,
                    variantsLength: finalVariants?.length,
                    variants: finalVariants
                  });
                  return finalVariants && finalVariants.length > 0 ? 
                    finalVariants.map((variant: any) => `${variant.variant} (${variant.type})`).join(', ') :
                    (Array.isArray(finalVariants) ? 'Not available' : 'Product variants information is being generated. This will include different sizes, colors, or formulations available for this product.');
                })()}
              </p>
            </div>
          </div>

          {/* Ingredients */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>Ingredients</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {discoveryData?.ingredients?.functional_groups && discoveryData.ingredients.functional_groups.length > 0 ? 
                  discoveryData.ingredients.functional_groups.map((group: any) => `${group.role}: ${group.ingredients.join(', ')}`).join('; ') :
                  'Product ingredients and materials are being analyzed. This will include key components, their functions, and quality specifications that contribute to the product performance.'
                }
              </p>
            </div>
          </div>

          {/* Steps To Use */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>Steps To Use</h4>
            <div className="flex-1">
              <div className="space-y-4">
                {discoveryData?.usage_steps && discoveryData.usage_steps.length > 0 ? (
                  discoveryData.usage_steps.map((step: any, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ 
                        borderRadius: '29.5px',
                        background: 'linear-gradient(180deg, #403691 0%, #5F4DBE 48.56%, #7C5EBF 100%)'
                      }}>
                        <span className="text-white text-sm font-medium">{index + 1}</span>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold mb-1" style={{ color: '#4A3B8E' }}>{step.step || `Step ${index + 1}`}</h5>
                        <p className="text-sm" style={{ color: '#85878B' }}>
                          {step.details || 'Details of the usage step.'}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">Loading usage steps from backend...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cross Sell */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>Cross Sell</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {discoveryData?.cross_sell_products && discoveryData.cross_sell_products.length > 0 ? 
                  discoveryData.cross_sell_products.map((product: any) => `${product.product} (${product.category})`).join(', ') :
                  'Cross-sell opportunities are being identified. This will include complementary products, accessories, and related items that customers might be interested in purchasing together.'
                }
              </p>
            </div>
          </div>

          {/* What Makes Us Different? */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>What Makes Us Different?</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {discoveryData?.differentiators && discoveryData.differentiators.length > 0 ? 
                  discoveryData.differentiators.map((diff: any) => `${diff.differentiator}: ${diff.evidence}`).join('; ') :
                  'Product differentiators are being analyzed. This will highlight unique features, competitive advantages, and distinctive qualities that set this product apart from competitors in the market.'
                }
              </p>
            </div>
          </div>

          {/* Founders Story */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>Founders Story</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {(() => {
                  console.log('Founder story debug:', {
                    hasFounderStory: !!finalFounderStory,
                    founderStory: finalFounderStory
                  });
                  return finalFounderStory || 'The founder story is being generated. This will include the inspiration behind the brand, key milestones, and the mission that drives the company forward.';
                })()}
              </p>
            </div>
          </div>

          {/* Certifications */}
          <div className="flex items-start" style={{ gap: '100px' }}>
            <h4 className="text-lg font-semibold flex-shrink-0" style={{ color: '#4A3B8E', minWidth: '220px' }}>Certifications</h4>
            <div className="flex-1">
              <p className="text-sm mb-2" style={{ color: '#85878B' }}>
                {(() => {
                  console.log('Certifications debug:', {
                    hasCertifications: !!finalCertifications,
                    certificationsLength: finalCertifications?.length,
                    certifications: finalCertifications
                  });
                  return finalCertifications && finalCertifications.length > 0 ? 
                    finalCertifications.map((cert: any) => `${cert.certification} (${cert.type})`).join(', ') :
                    (Array.isArray(finalCertifications) ? 'Not available' : 'Product certifications and approvals are being analyzed. This will include quality standards, safety certifications, and industry-specific approvals that validate the product quality.');
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default ResearchAgentDashboard; 
